import {
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  effect,
  signal,
  computed,
  input,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { CommonModule } from '@angular/common';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatDatepickerInput } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';
import { CopyableTruncateTooltipDirective } from '@app/shared/directives/copyable-truncate-tooltip.directive';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '@app/core/services/API/api-service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmPopup, ConfirmPopupResult } from '@app/shared/components/confirm-popup/confirm-popup';
import {
  AssigneeSelectionSlider,
  AssigneeUser,
} from '@app/shared/components/assignee-selection-slider/assignee-selection-slider';
import { AssigneeSelectionExpand } from '@app/shared/components/assignee-selection-expand/assignee-selection-expand';
import {
  ApprovalService,
  LogTimeService,
  ProjectsService,
  TaskService,
  UpdateService,
} from '@app/pages/projects/projects.service';
import { Spinner } from '@app/shared/components/spinner/spinner';
import {
  StageDisplayStatusPipe,
  StageOverviewStatusClassPipe,
} from '@app/shared/pipes/stage-status.pipe';
import { StatusSidebarRefreshService } from '@app/core/services/sidebar/status-sidebar-refresh/status-sidebar-refresh.service';
import { CrossTabSyncService } from '@app/core/services/cross-tab-sync/cross-tab-sync.service';
import { ProjectHoursService } from '@app/core/services/sidebar/project-hours.service';
import {
  parsePercentageComplete,
  resolvePercentageComplete,
} from '@app/core/utils/percentage-complete.util';
import { fromEvent, Subject, interval, of, forkJoin } from 'rxjs';
import { debounceTime, takeUntil, catchError, distinctUntilChanged, filter } from 'rxjs/operators';
import * as Highcharts from 'highcharts';
import { ApprovalRefreshService } from '@app/core/services/approval-refresh.service/approval-refresh.service';
import { APIContants } from '@app/constants/api';
import { SubmenuCountService } from '@app/core/services/sidebar/submenu-count.service';

interface Step {
  label: string;
  status: string;
}

interface StatusConfig {
  type: 'task' | 'issue' | 'project' | 'revision' | 'request' | 'custom';
  title: string;
  statusLabel: string;
  assignLabel: string;
  plannedHoursLabel?: string;
  dueDateLabel?: string;
  refNumberLabel?: string;
  showPlannedHours?: boolean;
  showDueDate?: boolean;
  showRefNumber?: boolean;
  showApprovals?: boolean;
  showTimeSummary?: boolean;
  customFields?: any[];
}

@Component({
  selector: 'app-status-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    MatDatepickerInput,
    MatDatepicker,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    ...matTooltipSmartImports,
    CopyableTruncateTooltipDirective,
    ConfirmPopup,
    AssigneeSelectionSlider,
    AssigneeSelectionExpand,
    Spinner,
    NgIf,
    StageDisplayStatusPipe,
    StageOverviewStatusClassPipe,
  ],
  templateUrl: './status-sidebar.html',
  styleUrl: './status-sidebar.scss',
})
export class StatusSidebar implements AfterViewInit, OnDestroy {
  // Configurable inputs
  itemId = input<string | null>(null);
  config = input.required<StatusConfig>();
  service = input.required<any>(); // Service to handle API calls
  updateMethod = input.required<string>(); // Method name for updating item
  getDetailsMethod = input.required<string>(); // Method name for getting item details
  refreshTrigger = input<number>(0);

  // State signals
  selectedTab = signal<'manage_item' | 'time_summary'>('manage_item');
  selectedStatus = signal<string | null>('Not Selected');
  selectedApproval = signal<string | null>(null);
  selectedStatusApproval = signal<string | null>(null);
  itemDetails = signal<any>(null);

  showAssignModal = signal(false);
  showAssignExpand = signal(false);
  showApprovalsModal = signal(false);
  assignedUsers = signal<AssigneeUser[]>([]);
  /** Expand assignee chips beyond the first two visible cards. */
  showAllAssignedUsers = signal(false);
  allAssignableData = signal<AssigneeUser[]>([]);
  statusList = signal<any[]>([]);
  statusListApproval = signal<any[]>([]);
  approvalList = signal<any[]>([]);
  isLoadingData = signal(false);
  isLoadingAssignedList = signal(false);
  isAssigningUsers = signal(false);
  steps = signal<Step[]>([]);
  userPreference = signal<any>(null);
  approvalPaths = signal<any>(null);
  private _activeStep = signal(0);
  private _visibleStepStart = signal(0);
  selectedStatusValue = signal<any>(null);
  displayApprovalRejectButton = signal(false);
  approverID = signal<string | null>(null);

  // Add signals for remove confirmation
  showRemoveConfirmPopup = signal(false);
  userToRemove = signal<AssigneeUser | null>(null);
  isRemovingAssignee = signal(false);
  showDeleteApprovalConfirmPopup = signal(false);
  approvalToDelete = signal<any>(null);

  showApprovalRemoveStageConfirmPopup = signal(false);
  pendingApprovalStageRemoval = signal<{
    pathIndex: number;
    stageIndex: number;
    stageName: string;
  } | null>(null);
  showApproveConfirmPopup = signal(false);
  showRejectConfirmPopup = signal(false);
  deletingAiHoursLogEntryId = signal<string | null>(null);
  approvalRemoveStageConfirmConfig = computed(() => {
    const p = this.pendingApprovalStageRemoval();
    const label = p?.stageName ? `"${p.stageName}"` : 'this stage';
    return {
      title: 'Remove stage',
      message: `Are you sure you want to remove ${label} from this approval path?`,
      type: 'warning' as const,
      showCancel: true,
      cancelText: 'Cancel',
      confirmText: 'Remove stage',
    };
  });

  // Planned hours and date signals
  copiedField = signal<'ref' | 'link' | null>(null);
  private copyFeedbackTimeout: ReturnType<typeof setTimeout> | null = null;
  showInput = signal(false);
  inputValue = signal('');
  submittedValue = signal('');
  originalPlannedHours = signal('');
  childExist = signal(false);
  selectedDate = signal<Date | null>(null);
  showDiv = signal(false);
  isUpdatingPlannedHours = signal(false);
  isUpdatingStatus = signal(false);
  isUpdatingDate = signal(false);

  // Planned hours tooltip
  @ViewChild('plannedHoursHintIconWrapper') plannedHoursHintIconWrapperRef!: ElementRef;
  @ViewChild('plannedHoursTooltip') plannedHoursTooltipRef!: ElementRef;
  showPlannedHoursTooltip = signal(false);
  plannedHoursTooltipPositioned = signal(false);
  plannedHoursHistory = signal<any[]>([]);
  private plannedHoursTooltipPositionRetryCount = 0;
  private readonly plannedHoursTooltipPositionMaxRetries = 5;
  private plannedHoursTooltipHideTimeout: ReturnType<typeof setTimeout> | null = null;

  // AI hours tooltip
  @ViewChild('aiHoursLogIconWrapper') aiHoursLogIconWrapperRef!: ElementRef;
  @ViewChild('aiHoursLogTooltip') aiHoursLogTooltipRef!: ElementRef;
  showAiHoursLog = signal(false);
  aiHoursLogTooltipPositioned = signal(false);
  aiHoursLog = signal<any[]>([]);
  showAiHoursLogPanel = signal(false);
  editingAiHoursLogEntryId = signal<string | null>(null);
  editingAiHoursLogEntryValue = signal('');
  private aiHoursLogTooltipPositionRetryCount = 0;
  private readonly aiHoursLogTooltipPositionMaxRetries = 5;
  private aiHoursLogTooltipHideTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Lets the cursor cross the gap between icon and fixed tooltip without closing. */
  private readonly hintTooltipHideDelayMs = 220;

  // AI hours inline edit
  aiHoursValue = signal<string>('0');
  aiHoursEditing = signal(false);
  aiHoursInputValue = signal('');
  originalAiHours = signal('');
  aiHoursHovered = signal(false);
  aiHoursFocused = signal(false);
  isUpdatingAiHours = signal(false);
  private aiHoursBlurSaveTimeout: ReturnType<typeof setTimeout> | null = null;
  readonly AI_HOURS_MAX = 24;

  // Signal to track the most recent edited value
  mostRecentEditedValue = signal<string | null>(null);

  // AI hours log
  isLoadingAiHoursLog = signal(false);
  /** True after AI hours log has been fetched for the current item (drives sidebar total from log rows). */
  aiHoursLogHasLoaded = signal(false);

  // Computed property to get the first AI hours log entry
  firstAiHoursLogEntry = computed(() => {
    const logs = this.aiHoursLog();
    return logs.length > 0 ? logs[0] : null;
  });

  /** Sidebar AI Hours readout — server total until log loads; then log row sum when entries exist. */
  readonly aiHoursSidebarDisplay = computed(() => {
    if (this.isLoadingAiHoursLog() || !this.aiHoursLogHasLoaded()) {
      return this.aiHoursValue();
    }
    const rows = this.aiHoursLogTableRows();
    if (rows.length > 0) {
      return this.formatAiHoursValue(this.sumAiHoursLogRowHours());
    }
    return this.aiHoursValue();
  });

  /** Hint when log exists or AI hours shown so tooltip can load */
  showAiHoursHistoryHint = computed(
    () =>
      this.aiHoursLog().length > 0 || this.toNumber(this.aiHoursSidebarDisplay()) > 0
  );

  /** Per API log line: hour saved this action (same user can appear multiple times) */
  readonly aiHoursLogTableRows = computed(() =>
    this.buildAiHoursTableRows(this.aiHoursLog())
  );

  /** Newest-first for tooltip table */
  readonly aiHoursDisplayRows = computed(() => {
    const rows = [...this.aiHoursLogTableRows()];
    return rows.sort((a, b) => {
      const tb = b.createdAt?.getTime() ?? 0;
      const ta = a.createdAt?.getTime() ?? 0;
      return tb - ta;
    });
  });

  readonly aiHoursLogHeaderTotalDisplay = computed(() => {
    const sum = this.sumAiHoursLogRowHours();
    return this.formatAiHoursValue(sum);
  });

  // Add signals for drag detection
  private isDragging = signal(false);
  private mouseDownPosition = signal<{ x: number; y: number } | null>(null);

  // Add this signal to store the pathStartStatusList
  pathStartStatusList = signal<string[]>([]);

  // Add signals for approvals modal
  assignList = signal<any[]>([]);
  isLoadingApprovals = signal(false);
  isSavingApproval = signal(false);
  isApprovingApproval = signal(false);
  isRejectingApproval = signal(false);
  isUpdatingAsCompleted = signal(false);
  singleApprovalData = signal<any>(null);
  approvalExists = signal<boolean>(false); // Track if approval exists based on API response
  originalApprovalData = signal<any>(null); // Store original data to reset when modal is closed without saving
  private requestCreatedBy = signal<any | null>(null);
  private projectOwner = signal<any | null>(null);

  private static readonly SEND_TO_REQUESTOR_STAGE = 'Send to Requestor';
  private static readonly SEND_TO_PROJECT_OWNER_STAGE = 'Send to TWCAN PM/Owner for Approval';
  
  // Validation signals for approvals
  approvalValidationErrors = signal<string[]>([]);
  showApprovalValidationErrors = signal(false);
  approvalInvalidFields = signal<Set<string>>(new Set());
  // Trigger signal to force computed validation to recompute when approval data is mutated
  approvalDataChangeTrigger = signal(0);

  // Task percentage for time summary
  taskPercentage = signal<number>(0);

  // Time summary data signals
  taskHourData = signal<any>(null);
  taskHourStatus = signal<any>(null);
  isTimeSummaryLoading = signal<boolean>(false);
  isChartsLoading = signal<boolean>(false);
  hasChartError = signal<boolean>(false);

  // Chart instances and timeouts
  private chartInstances: any[] = [];
  private loadingTimeout: any = null;
  private fallbackTimeout: any = null;
  private destroy$ = new Subject<void>();
  private lastProcessedDataHash: string | null = null;
  private isProcessingCacheUpdate = false;
  private lastApprovalRefreshTrigger = 0;

  /**
   * Per-stage filtered lists for approval ng-select. Map avoids one stage evicting
   * another’s cache (which caused new array refs every CD and flashing dropdown DOM).
   */
  private approvalAvailableAssigneesCacheMap = new Map<string, any[]>();

  // Helper method to check if status is complete/closed
  isStatusComplete(): boolean {
    const statusName = this.selectedStatusValue()?.toLowerCase();
    return statusName === 'complete' || statusName === 'closed';
  }

  /** True when the current status label includes Pending Approval (path-start / awaiting approval). */
  isStatusPendingApproval(): boolean {
    const selectedId = this.selectedStatus();
    if (selectedId && this.pathStartStatusList().includes(selectedId)) {
      return true;
    }
    return String(this.statusLabel ?? '')
      .toLowerCase()
      .includes('pending approval');
  }

  /** Always show 2 assignee cards until the user clicks +n More. */
  private readonly collapsedAssignedVisible = 2;

  readonly visibleAssignedUsers = computed(() => {
    const users = this.assignedUsers();
    if (this.showAllAssignedUsers()) {
      return users;
    }
    return users.slice(0, this.collapsedAssignedVisible);
  });

  readonly hiddenAssignedCount = computed(() =>
    Math.max(0, this.assignedUsers().length - this.collapsedAssignedVisible)
  );

  readonly showUnassignedLabel = computed(
    () => this.itemDetails() != null && this.assignedUsers().length === 0
  );

  readonly showAssignedListLoader = computed(
    () => this.isLoadingAssignedList() && this.assignedUsers().length === 0
  );

  readonly hasMoreAssignedUsers = computed(
    () => this.assignedUsers().length > this.collapsedAssignedVisible
  );


  readonly isCurrentUserAssignee = computed(() => {
    const fromList = this.assignedUsers();
    const assignees =
      fromList.length > 0 ? fromList : (this.itemDetails()?.assignees ?? []);
    if (!Array.isArray(assignees) || assignees.length === 0) {
      return false;
    }

    const userID = localStorage.getItem('userID');
    const jobRoleID = localStorage.getItem('jobRoleID');
    const homeTeamID = localStorage.getItem('homeTeamID');

    return assignees.some(
      (assignee: any) =>
        String(assignee?._id) === String(userID) ||
        String(assignee?._id) === String(jobRoleID) ||
        String(assignee?._id) === String(homeTeamID)
    );
  });

  /**
   * Assignee gate for AI hours: matches logged-in user via userID, jobRoleID, or homeTeamID.
   * Projects (and other non-assignee types) remain editable for all users.
   */
  readonly canEditAiHours = computed(() => {
    const type = this.config()?.type;
    if (type === 'task' || type === 'issue' || type === 'request' || type === 'revision') {
      return this.isCurrentUserAssignee();
    }
    return true;
  });

  /**
   * Full AI-hours edit gate: blocked when status is complete/closed, except while
   * Pending Approval; still requires canEditAiHours (homeTeamID / assignee check).
   */
  readonly canModifyAiHours = computed(() => {
    if (!this.canEditAiHours()) {
      return false;
    }
    if (this.isStatusPendingApproval()) {
      return true;
    }
    return !this.isStatusComplete();
  });

  /** Show Update As Completed only when the logged-in user is assigned, has assignmentStatus working, and the object has multiple assignees. */
  readonly showUpdateAsCompletedButton = computed(() => {
    if (!this.itemId() || this.isStatusComplete()) {
      return false;
    }

    const assignees = this.itemDetails()?.assignees ?? this.assignedUsers();
    if (!Array.isArray(assignees) || assignees.length < 2) {
      return false;
    }

    const userID = localStorage.getItem('userID');
    const jobRoleID = localStorage.getItem('jobRoleID');
    const homeTeamID = localStorage.getItem('homeTeamID');

    return assignees.some((assignee: any) => {
      const isLoggedUserAssignee =
        assignee?._id === userID ||
        assignee?._id === jobRoleID ||
        assignee?._id === homeTeamID;

      return (
        isLoggedUserAssignee &&
        String(assignee?.assignmentStatus ?? '').toLowerCase() === 'working'
      );
    });
  });

  toggleAssignedListExpand(): void {
    this.showAllAssignedUsers.update((open) => !open);
  }

  private syncAssignedListExpand(): void {
    if (this.assignedUsers().length <= this.collapsedAssignedVisible) {
      this.showAllAssignedUsers.set(false);
    }
  }

// Helper method to convert string to number for template usage
  toNumber(value: string | number): number {
    return Number(value) || 0;
  }
  // ViewChilds
  @ViewChild('inputBox') inputBox!: ElementRef;
  @ViewChild('aiHoursInputRef') aiHoursInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('picker') picker!: MatDatepicker<Date>;
  @ViewChild('taskStatusChartContainer') taskStatusChartContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('balanceHoursChartContainer') balanceHoursChartContainer!: ElementRef<HTMLDivElement>;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private toastr: ToastrService,
    private projectsService: ProjectsService,
    private approvalService: ApprovalService,
    private statusSidebarRefreshService: StatusSidebarRefreshService,
    private logtimeService: LogTimeService,
    private crossTabSyncService: CrossTabSyncService,
    private updateService: UpdateService,
    private projectHoursService: ProjectHoursService,
    private approvalRefreshService: ApprovalRefreshService,
    private submenuCountService: SubmenuCountService
  ) {
    // Initialization effect
    effect(() => {
      const id = this.itemId();
      if (id) {
        // Reset approvalExists when item changes
        this.approvalExists.set(false);
        this.displayApprovalRejectButton.set(false);
        this.approverID.set(null);
        this.showAllAssignedUsers.set(false);
        this.aiHoursLogHasLoaded.set(false);
        this.aiHoursLog.set([]);
        this.getItemDetails(id);
        this.getAssignList();
        this.getStatus();
        this.getApprovals();
        this.getApprovalTracking(id);
        this.loadAssignList();
      }
    });
    // Refresh trigger effect
    effect(() => {
      const trigger = this.refreshTrigger();
      const id = this.itemId();
      if (trigger > 0 && id) {
        // Clear cache and refresh data when trigger changes
        if (this.service().clearSingleTaskCache) {
          this.service().clearSingleTaskCache(id);
        }
        if (this.service().clearSingleIssueCache) {
          this.service().clearSingleIssueCache(id);
        }
        if (this.service().clearSingleProjectCache) {
          this.service().clearSingleProjectCache(id);
        }
        if (this.service().clearSingleRevisionCache) {
          this.service().clearSingleRevisionCache(id);
        }
        if (this.service().clearSingleRequestCache) {
          this.service().clearSingleRequestCache(id);
        }

        // Refresh the item details
        this.getItemDetails(id);
        this.getApprovalTracking(id);
      }
    });

    // Approval refresh effect - listen for changes from approvals component
    effect(() => {
      const approvalRefreshTrigger = this.approvalRefreshService.refreshTrigger();
      const id = this.itemId();
      if (approvalRefreshTrigger > this.lastApprovalRefreshTrigger && id) {
        this.lastApprovalRefreshTrigger = approvalRefreshTrigger;
        // Clear cache before refreshing to ensure fresh data
        if (this.service().clearSingleTaskCache) {
          this.service().clearSingleTaskCache(id);
        }
        if (this.service().clearSingleIssueCache) {
          this.service().clearSingleIssueCache(id);
        }
        if (this.service().clearSingleProjectCache) {
          this.service().clearSingleProjectCache(id);
        }
        if (this.service().clearSingleRevisionCache) {
          this.service().clearSingleRevisionCache(id);
        }
        if (this.service().clearSingleRequestCache) {
          this.service().clearSingleRequestCache(id);
        }
        // Refresh approval-related data when approvals component makes changes
        this.getApprovals();
        // getItemDetails will handle calling getSingleApproval if approvalProcess exists
        // Don't check selectedApproval here as it might have stale data
        this.getItemDetails(id);
        this.getApprovalTracking(id);
      }
    });

  }

  visibleSteps = computed(() => {
    const steps = this.steps();
    if (steps.length <= 3) return steps;
    const start = Math.max(
      0,
      Math.min(this.visibleStepStart, steps.length - 3)
    );
    return steps.slice(start, start + 3);
  });

  canGoPrev = computed(() => this.visibleStepStart > 0);
  canGoNext = computed(() => {
    const steps = this.steps();
    return steps.length > 3 && this.visibleStepStart < steps.length - 3;
  });

  // Getter for template iteration
  get visibleStepsArray() {
    return this.visibleSteps();
  }

  // Getters for template access
  get visibleStepStart() {
    return this._visibleStepStart();
  }
  get activeStep() {
    return this._activeStep();
  }

  // Getter to provide the display label for the selected status
  get statusLabel() {
    const status = this.statusListWithApproval().find(
      (s) => s.value === this.selectedStatus()
    );
    return status ? status.label : 'Not Selected';
  }

  // Getter to provide the color for the selected status
  get statusColor() {
    const status = this.statusList().find(
      (s) => s.value === this.selectedStatus()
    );
    return status ? status.color : '#979BA8'; // Default color if no status selected
  }

  // Modal methods
  openAssignModal = () => {
    // Prevent opening modal for parent tasks with children
    // if (this.childExist()) {
    //   return;
    // }
    // Toggle the expand/collapse state
    this.showAssignExpand.set(!this.showAssignExpand());
  };

  closeAssignModal = () => {
    this.showAssignModal.set(false);
  };
  
  closeAssignExpand = () => {
    this.showAssignExpand.set(false);
  };

  openApprovalsModal = () => {
    this.showApprovalsModal.set(true);
    
    // Always clear backup first to ensure fresh backup
    this.originalApprovalData.set(null);

    // Auto-fill requestor / project-owner stages before snapshotting backup
    this.applyAutoFilledApprovers(this.getSelectedApprovalDetails());
    
    // Store original approval data for potential reset
    const currentApprovalData = this.getSelectedApprovalDetails();
    if (currentApprovalData) {
      // Create a deep copy to avoid reference issues
      this.originalApprovalData.set(JSON.parse(JSON.stringify(currentApprovalData)));
    } else {
      this.originalApprovalData.set(null);
    }
    
    if (!this.singleApprovalData()) {
      this.selectedApproval.set(null);
    }
  };

  closeApprovalsModal = () => {
    this.showApprovalsModal.set(false);
    this.selectedStatusApproval.set(null);
    this.showApprovalValidationErrors.set(false);
    
    // Reset approval data to original state when closing without saving
    const originalData = this.originalApprovalData();
    if (originalData) {
      // Reset to original data for both existing and new approvals
      if (this.singleApprovalData()) {
        // For existing approvals, restore from stored original data
        this.singleApprovalData.set(JSON.parse(JSON.stringify(originalData)));
      } else {
        // For new approvals from list, find and reset the approval in the list
        const approvalList = this.approvalList();
        const approvalIndex = approvalList.findIndex(
          (approval) => approval._id === this.selectedApproval()
        );
        if (approvalIndex !== -1) {
          // Replace the entire approval object to restore original state
          approvalList[approvalIndex] = JSON.parse(JSON.stringify(originalData));
          this.approvalList.set([...approvalList]);
        }
      }
    } else {
      // No original data, clear selection for new approvals
      this.selectedApproval.set(null);
    }
    
    // Clear the original data
    this.originalApprovalData.set(null);
  };

  // Validation method for approvals - pure function (no side effects)
  validateApprovalData(approval: any | null): { isValid: boolean; errors: string[]; invalidFields: Set<string> } {
    const errors: string[] = [];
    const invalidFieldsSet = new Set<string>();
    
    if (!approval || !approval.approvalPaths || approval.approvalPaths.length === 0) {
      errors.push('At least one approval path is required');
      return { isValid: false, errors, invalidFields: invalidFieldsSet };
    }

    approval.approvalPaths.forEach((path: any, pathIndex: number) => {
      // Validate path start status
      if (!path.pathStartStatus || (typeof path.pathStartStatus === 'string' && path.pathStartStatus.trim() === '')) {
        errors.push(`Path ${path.path}: Start status is required`);
        invalidFieldsSet.add(`path-${pathIndex}-startStatus`);
      }

      // Validate reject status - can be 'previousStatus' or a valid status ID, but not null or empty string
      if (!path.rejectStatus || 
          (path.rejectStatus !== 'previousStatus' && 
           (typeof path.rejectStatus === 'string' && path.rejectStatus.trim() === ''))) {
        errors.push(`Path ${path.path}: Rejection status is required`);
        invalidFieldsSet.add(`path-${pathIndex}-rejectStatus`);
      }

      // Validate stages
      if (!path.stages || path.stages.length === 0) {
        errors.push(`Path ${path.path}: At least one stage is required`);
      } else {
        path.stages.forEach((stage: any, stageIndex: number) => {
          // Validate stage name
          if (!stage.stageName || stage.stageName.trim() === '' || stage.stageName.trim() === 'New Stage') {
            errors.push(`Path ${path.path}, Stage ${stage.stageNumber}: Stage name is required`);
            invalidFieldsSet.add(`path-${pathIndex}-stage-${stageIndex}-name`);
          }

          // Validate approvers
          if (!stage.approvers || stage.approvers.length === 0) {
            errors.push(`Path ${path.path}, Stage ${stage.stageNumber}: At least one approver is required`);
            invalidFieldsSet.add(`path-${pathIndex}-stage-${stageIndex}-approvers`);
          }
        });
      }
    });

    return { isValid: errors.length === 0, errors, invalidFields: invalidFieldsSet };
  }

  // Computed signal for approval validation results
  approvalValidationResult = computed(() => {
    // Include trigger in dependencies to force recomputation when approval data is mutated
    this.approvalDataChangeTrigger();
    const approval = this.getSelectedApprovalDetails();
    return this.validateApprovalData(approval);
  });

  // Computed signal for approval invalid fields - always computed to show real-time validation state
  approvalInvalidFieldsComputed = computed(() => {
    return this.approvalValidationResult().invalidFields;
  });

  // Computed signal for approval validation errors - only computed when validation errors are shown
  approvalValidationErrorsComputed = computed(() => {
    if (!this.showApprovalValidationErrors()) {
      return [];
    }
    return this.approvalValidationResult().errors;
  });

  // Helper methods to check if specific fields are invalid - always check validation state
  isApprovalPathStartStatusInvalid(pathIndex: number): boolean {
    return this.approvalInvalidFieldsComputed().has(`path-${pathIndex}-startStatus`);
  }

  isApprovalPathRejectStatusInvalid(pathIndex: number): boolean {
    return this.approvalInvalidFieldsComputed().has(`path-${pathIndex}-rejectStatus`);
  }

  isApprovalStageNameInvalid(pathIndex: number, stageIndex: number): boolean {
    return this.approvalInvalidFieldsComputed().has(`path-${pathIndex}-stage-${stageIndex}-name`);
  }

  isApprovalStageApproversInvalid(pathIndex: number, stageIndex: number): boolean {
    return this.approvalInvalidFieldsComputed().has(`path-${pathIndex}-stage-${stageIndex}-approvers`);
  }

  // Computed signal for approval form validity - always validate to ensure submit button is properly enabled/disabled
  isApprovalFormValid = computed(() => {
    return this.approvalValidationResult().isValid;
  });

  saveApprovals = () => {
    const approval = this.getSelectedApprovalDetails();
    if (!approval) {
      this.toastr.error('No approval selected for saving');
      return;
    }

    // Validate before saving - use computed result
    const validation = this.approvalValidationResult();
    if (!validation.isValid) {
      this.showApprovalValidationErrors.set(true);
      return;
    }

    this.showApprovalValidationErrors.set(false);

    const currentObjID = this.itemId();

    // Determine if this is an update or create operation
    const isUpdate = this.singleApprovalData() !== null;

    // Prepare request body according to the specified structure
    const requestBody = isUpdate
      ? {
          approvalID: approval._id, // For updates, use approvalID instead of masterID
          objCode: this.getItemIdFieldCode(), // Current object code (ISSUE, PROJECT, etc.)
          objID: currentObjID,
          approvalName: approval.approvalName,
          approvalDescription: approval.approvalDescription || '',
          approvalPaths: approval.approvalPaths.map((path: any) => ({
            path: path.path,
            pathStartStatus: path.pathStartStatus,
            rejectStatus: path.rejectStatus,
            isApproved: path.isApproved,
            stages: path.stages.map((stage: any) => ({
              stageNumber: stage.stageNumber,
              stageName: stage.stageName,
              approvers: stage.approvers.map((approver: any) => ({
                type: approver.type || '',
                name: approver.name,
                _id: approver._id,
                status: approver.status || 'pending',
              })),
              isOneDecision: stage.isOneDecision || false,
              approvalCondition: stage.approvalCondition || '',
              status: stage.status || '',
              approvedBy: stage.approvedBy || [],
              rejectedBy: stage.rejectedBy || [],
            })),
          })),
          currentStage: approval.currentStage || 1,
          currentPath: approval.currentPath || 1,
          status: approval.status || '',
        }
      : {
          masterID: approval._id, // For creates, use masterID
          objCode: this.getItemIdFieldCode(), // Current object code (ISSUE, PROJECT, etc.)
          objID: currentObjID, // Current object ID from route
          approvalName: approval.approvalName,
          approvalDescription: approval.approvalDescription || '',
          approvalPaths: approval.approvalPaths.map((path: any) => ({
            path: path.path,
            pathStartStatus: path.pathStartStatus,
            rejectStatus: path.rejectStatus,
            isApproved: path.isApproved,
            stages: path.stages.map((stage: any) => ({
              stageNumber: stage.stageNumber,
              stageName: stage.stageName,
              approvers: stage.approvers.map((approver: any) => ({
                type: approver.type || '',
                name: approver.name,
                _id: approver._id,
               status: approver.status || 'pending',
              })),
              isOneDecision: stage.isOneDecision || false,
              approvalCondition: stage.approvalCondition || '',
              status: stage.status || '',
              approvedBy: stage.approvedBy || [],
              rejectedBy: stage.rejectedBy || [],
            })),
          })),
          currentStage: approval.currentStage || 1,
          currentPath: approval.currentPath || 1,
          status: approval.status || '',
        };

    this.isSavingApproval.set(true);
    if (isUpdate) {
      // Call the updateNewApproval API for existing approvals
      this.approvalService.updateNewApproval(requestBody).subscribe({
        next: (res: any) => {
          this.toastr.success(res.message || 'Approval updated successfully');
          this.closeApprovalsModal();
          this.approvalExists.set(true);
          this.getApprovalTracking(this.itemId());
          // Trigger refresh for approvals component
          this.approvalRefreshService.triggerRefresh();
          this.isSavingApproval.set(false);
          // Clear original data since save was successful
          this.originalApprovalData.set(null);
        },
        error: (err: any) => {
          console.error('Error updating approval:', err);
          this.toastr.error(err?.message || 'Error updating approval');
          this.isSavingApproval.set(false);
        },
      });
    } else {
      // Call the createNewApproval API for new approvals
      this.approvalService.createNewApproval(requestBody).subscribe({
        next: (res: any) => {
          this.toastr.success(res?.message || 'Approval saved successfully');
          this.closeApprovalsModal();
          // Clear cache before refreshing to ensure fresh data with new approval
          const id = this.itemId();
          if (id) {
            if (this.service().clearSingleTaskCache) {
              this.service().clearSingleTaskCache(id);
            }
            if (this.service().clearSingleIssueCache) {
              this.service().clearSingleIssueCache(id);
            }
            if (this.service().clearSingleProjectCache) {
              this.service().clearSingleProjectCache(id);
            }
            if (this.service().clearSingleRevisionCache) {
              this.service().clearSingleRevisionCache(id);
            }
            if (this.service().clearSingleRequestCache) {
              this.service().clearSingleRequestCache(id);
            }
          }
          // Refresh item details first to get the updated approvalProcess ID
          this.getItemDetails(this.itemId());
          if (res?.success && res?.data) {
            // Set the approval data from response
            this.singleApprovalData.set(res.data);
            this.selectedApproval.set(res.data._id);
            this.approvalExists.set(true);
          }
          // Trigger refresh for approvals component
          this.approvalRefreshService.triggerRefresh();
          this.isSavingApproval.set(false);
          // Clear original data since save was successful
          this.originalApprovalData.set(null);
        },
        error: (err: any) => {
          this.toastr.error(err?.message || 'Error saving approval');
          this.isSavingApproval.set(false);
        },
      });
    }
  };

  // Handle assign users from shared component
  onAssignUsers = (event: {
    newlySelectedUsers: AssigneeUser[];
    removedUsers: AssigneeUser[];
  }) => {
    const { newlySelectedUsers, removedUsers } = event;

    if (newlySelectedUsers.length === 0 && removedUsers.length === 0) {
      return;
    }

    const uniqueAssignedUsersMap = new Map<string, AssigneeUser>();
    this.assignedUsers().forEach((user) => {
      uniqueAssignedUsersMap.set(user._id, user);
    });

    // Add newly selected users
    newlySelectedUsers.forEach((user) => {
      uniqueAssignedUsersMap.set(user._id, user);
    });

    // Remove users that were removed
    removedUsers.forEach((user) => {
      uniqueAssignedUsersMap.delete(user._id);
    });

    this.assignedUsers.set(Array.from(uniqueAssignedUsersMap.values()));
    this.syncAssignedListExpand();

    if (this.itemId()) {
      this.isAssigningUsers.set(true);
      const params: Record<string, any> = {
        [this.getItemIdField()]: this.itemId(),
      };
      if (newlySelectedUsers.length > 0) {
        params['assignees'] = newlySelectedUsers;
      }
      if (removedUsers.length > 0) {
        params['removedAssignees'] = removedUsers;
      }
      this.updateItem(params, false, true); // Pass true to indicate this is an assignee update
    }
  };

  // Handler to open remove confirmation popup
  openRemoveConfirmPopup = (user: AssigneeUser) => {
    // Prevent removing users for parent tasks with children
    // if (this.childExist()) {
    //   return;
    // }
    this.userToRemove.set(user);
    this.showRemoveConfirmPopup.set(true);
  };

  // Handler to confirm removal
  confirmRemoveAssignedUser = () => {
    const user = this.userToRemove();
    if (user) {
      this.isRemovingAssignee.set(true);
      this.removeAssignedUser(user);
    }
  };

  // Handler to cancel removal
  cancelRemoveAssignedUser = () => {
    this.showRemoveConfirmPopup.set(false);
    this.userToRemove.set(null);
    this.isRemovingAssignee.set(false);
  };

  // Handler to open delete approval confirmation popup
  openDeleteApprovalConfirmPopup = () => {
    this.approvalToDelete.set(this.singleApprovalData());
    this.showDeleteApprovalConfirmPopup.set(true);
  };

  // Handler to confirm approval deletion
  confirmDeleteApproval = () => {
    const approval = this.approvalToDelete();
    if (approval && approval._id) {
      this.deleteApproval(approval._id);
      // Don't close popup here - let deleteApproval handle it after API call completes
    } else {
      this.showDeleteApprovalConfirmPopup.set(false);
      this.approvalToDelete.set(null);
    }
  };

  // Handler to cancel approval deletion
  cancelDeleteApproval = () => {
    this.showDeleteApprovalConfirmPopup.set(false);
    this.approvalToDelete.set(null);
  };

  // Change removeAssignedUser to only do the removal (no popup logic)
  removeAssignedUser = (user: AssigneeUser) => {
    this.assignedUsers.set(
      this.assignedUsers().filter((u) => u._id !== user._id)
    );
    this.syncAssignedListExpand();
    this.updateItem({
      [this.getItemIdField()]: this.itemId(),
      removedAssignees: [user],
    }, true); // Pass true to indicate this is an assignee removal
  };
  

  getDisplayName = (item: any): string => {
    if (!item) return 'Unknown';
    return (
      item.name ||
      item.jobrole ||
      item.teamName ||
      item.jobroles?.jobrole ||
      'Unknown'
    );
  };

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    // Store mouse down position for drag detection
    this.mouseDownPosition.set({ x: event.clientX, y: event.clientY });
    this.isDragging.set(false);
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent) {
    // Detect if user is dragging (mouse moved more than 5px from initial position)
    if (this.mouseDownPosition()) {
      const deltaX = Math.abs(event.clientX - this.mouseDownPosition()!.x);
      const deltaY = Math.abs(event.clientY - this.mouseDownPosition()!.y);

      if (deltaX > 5 || deltaY > 5) {
        this.isDragging.set(true);
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // AI hours: save on click outside .ai_hrs_wrap when editing
    if (this.aiHoursEditing()) {
      const isInsideAiHrsWrap = target.closest('.ai_hrs_wrap') !== null;
      if (!isInsideAiHrsWrap) {
        this.saveAiHours();
      }
    }

    // Planned hours input click-outside logic - only close if clicked outside and not dragging
    if (this.showInput() && this.inputBox && !this.isDragging()) {
      const inputWrapper = target.closest('.input_wrap');
      const isClickInsideInput = this.inputBox.nativeElement.contains(
        event.target
      );
      const isClickInsideWrapper = inputWrapper !== null;

      // Only close if clicked outside both the input and its wrapper, and not dragging
      if (!isClickInsideInput && !isClickInsideWrapper) {
        this.submittedValue.set(this.inputValue() || this.submittedValue());
        this.showInput.set(false);
        this.onPlannedHoursChange();
      }
    }

    // Reset drag state after click
    this.isDragging.set(false);
    this.mouseDownPosition.set(null);
  }

  /** Full hash-location URL for the current sidebar item (task/issue/request/etc.). */
  itemDetailsLink = computed(() => {
    const id = this.itemId()?.trim();
    if (!id) {
      return null;
    }

    const routeByType: Record<StatusConfig['type'], string | null> = {
      task: `/task/task-details/${id}`,
      issue: `/issue/issue-details/${id}`,
      request: `/request/request-details/${id}`,
      revision: `/revision/revision-details/${id}`,
      project: `/projects/project-details/${id}`,
      custom: null,
    };

    const path = routeByType[this.config()?.type];
    if (!path) {
      return null;
    }

    return `${window.location.origin}/#${path}`;
  });

  itemReferenceNumber = computed(() => {
    const details = this.itemDetails();
    return (
      details?.taskReferenceNumber ||
      details?.issueReferenceNumber ||
      details?.projectReferenceNumber ||
      details?.revisionReferenceNumber ||
      details?.requestReferenceNumber ||
      ''
    );
  });

  copyRefNo = async (event?: Event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const trimmedRef = String(this.itemReferenceNumber() || '').trim();
    if (!trimmedRef) {
      return;
    }

    if (await this.writeClipboardText(trimmedRef)) {
      this.flashCopied('ref');
    }
  };

  copyItemLink = async (event?: Event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const itemLink = this.itemDetailsLink();
    if (!itemLink) {
      return;
    }

    const trimmedRef = String(this.itemReferenceNumber() || '').trim();
    const copied = trimmedRef
      ? await this.writeClipboardLinkedText(trimmedRef, itemLink)
      : await this.writeClipboardText(itemLink);

    if (copied) {
      this.flashCopied('link');
    }
  };

  private async writeClipboardText(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  /** Copies `label` as a hyperlink to `url` for rich paste; plain text keeps both values. */
  private async writeClipboardLinkedText(label: string, url: string): Promise<boolean> {
    const html = this.buildCenteredLinkCellHtml(label, url);
    const plainText = `${label}\n${url}`;

    if (await this.writeClipboardHtml(html, plainText)) {
      return true;
    }

    if (this.copyLinkedTextViaSelection(label, url)) {
      return true;
    }

    return this.writeClipboardText(plainText);
  }

  private async writeClipboardHtml(html: string, plainText: string): Promise<boolean> {
    try {
      if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
        return false;
      }

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);
      return true;
    } catch {
      return false;
    }
  }

  /** Spreadsheets right-align numeric values unless the pasted cell carries its own alignment. */
  private buildCenteredLinkCellHtml(label: string, url: string): string {
    return (
      '<table><tr><td align="center" style="text-align:center;">' +
      `<a href="${this.escapeHtml(url)}">${this.escapeHtml(label)}</a>` +
      '</td></tr></table>'
    );
  }

  private copyLinkedTextViaSelection(label: string, url: string): boolean {
    const container = document.createElement('div');
    container.innerHTML = this.buildCenteredLinkCellHtml(label, url);
    container.setAttribute('style', 'position:fixed;left:-9999px;top:0;');
    document.body.appendChild(container);

    const selection = window.getSelection();
    if (!selection) {
      document.body.removeChild(container);
      return false;
    }

    const range = document.createRange();
    range.selectNode(container);
    selection.removeAllRanges();
    selection.addRange(range);

    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      selection.removeAllRanges();
      document.body.removeChild(container);
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private flashCopied(field: 'ref' | 'link'): void {
    this.copiedField.set(field);
    if (this.copyFeedbackTimeout) {
      clearTimeout(this.copyFeedbackTimeout);
    }
    this.copyFeedbackTimeout = setTimeout(() => {
      this.copiedField.set(null);
      this.copyFeedbackTimeout = null;
    }, 2000);
  }

  onClickInside = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  // Planned hours tooltip handlers
  onPlannedHoursHintEnter = () => {
    if (this.plannedHoursTooltipHideTimeout) {
      clearTimeout(this.plannedHoursTooltipHideTimeout);
      this.plannedHoursTooltipHideTimeout = null;
    }
    this.showPlannedHoursTooltip.set(true);
    this.plannedHoursTooltipPositioned.set(false);
    this.plannedHoursTooltipPositionRetryCount = 0;
    
    requestAnimationFrame(() => {
      this.positionPlannedHoursTooltip();
    });
  };

  onPlannedHoursHintLeave = (event?: MouseEvent) => {
    if (event && 'buttons' in event && (event as MouseEvent).buttons !== 0) {
      const onUp = () => this.schedulePlannedHoursTooltipHide();
      document.addEventListener('pointerup', onUp, { once: true });
      return;
    }
    this.schedulePlannedHoursTooltipHide();
  };

  private schedulePlannedHoursTooltipHide(): void {
    if (this.plannedHoursTooltipHideTimeout) {
      clearTimeout(this.plannedHoursTooltipHideTimeout);
    }
    this.plannedHoursTooltipHideTimeout = setTimeout(() => {
      this.plannedHoursTooltipHideTimeout = null;
      this.showPlannedHoursTooltip.set(false);
    }, this.hintTooltipHideDelayMs);
  }

  /** Stops enableInput() / input_wrap click handlers from stealing tooltip clicks and text selection. */
  onCompletionLogTooltipPointerBlock = (event: Event) => {
    event.stopPropagation();
  };

  positionPlannedHoursTooltip() {
    if (!this.plannedHoursHintIconWrapperRef || !this.plannedHoursTooltipRef) {
      if (this.plannedHoursTooltipPositionRetryCount < this.plannedHoursTooltipPositionMaxRetries) {
        this.plannedHoursTooltipPositionRetryCount++;
        setTimeout(() => this.positionPlannedHoursTooltip(), 20);
      }
      return;
    }
    const iconRect = this.plannedHoursHintIconWrapperRef.nativeElement.getBoundingClientRect();
    const tooltipEl = this.plannedHoursTooltipRef.nativeElement as HTMLElement;
    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;
    const gap = 2;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = iconRect.bottom + gap;
    // For right sidebar, it's safer to right-align the tooltip to the icon to avoid cutoff by sidebar left edge
    let left = iconRect.right - tooltipWidth;

    // Horizontal adjustments - Ensure it doesn't go off-screen to the left
    if (left < 8) left = 8;
    // Ensure it doesn't go off-screen to the right
    if (left + tooltipWidth > viewportWidth - 8) {
      left = viewportWidth - tooltipWidth - 8;
    }

    // Vertical adjustments
    if (top + tooltipHeight > viewportHeight - 8) {
      top = iconRect.top - tooltipHeight - gap;
      if (top < 8) top = 8;
    }

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    this.plannedHoursTooltipPositioned.set(true);
  }

  // Input-specific event handlers to prevent unwanted closures during text selection
  onInputMouseDown = (event: MouseEvent) => {
    // Prevent drag detection when clicking inside input
    this.isDragging.set(false);
    event.stopPropagation();
  };

  onInputMouseUp = (event: MouseEvent) => {
    // Prevent drag detection when releasing mouse inside input
    this.isDragging.set(false);
    event.stopPropagation();
  };

  onInputSelectStart = (event: Event) => {
    // Prevent drag detection when text selection starts
    this.isDragging.set(false);
    event.stopPropagation();
  };

  // Handle keydown events specifically for Escape key
  onInputKeyDown = (event: KeyboardEvent) => {
    // Handle Escape key - cancel editing without updating value
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      // Reset to original value and close input
      this.inputValue.set(this.originalPlannedHours());
      this.showInput.set(false);
      // Reset loading state if it was active
      this.isUpdatingPlannedHours.set(false);
      return;
    }
  };

  enableInput = () => {
    // Prevent enabling input for parent tasks with children
    if (this.childExist() || this.isStatusComplete()) {
      return;
    }
    this.showInput.set(true);
    this.originalPlannedHours.set(this.submittedValue());
    this.inputValue.set(this.submittedValue());
    // Use requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      if (this.inputBox?.nativeElement) {
        this.inputBox.nativeElement.focus();
        // Select all text for easy editing
        this.inputBox.nativeElement.select();
      }
    });
  };

  allowOnlyDecimalNumbers = (event: KeyboardEvent) => {
    const inputChar = event.key;
    const currentValue = this.inputValue() || '';

    // Handle Enter key - save and close input
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.submittedValue.set(this.inputValue() || this.submittedValue());
      this.showInput.set(false);
      this.onPlannedHoursChange();
      return;
    }

    // Allow only decimal numbers
    if (!/[\d.]/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (inputChar === '.' && currentValue.includes('.')) {
      event.preventDefault();
      return;
    }

    // Limit length to 5 characters (unless text is selected)
    if (currentValue.length >= 5 && !this.isTextSelected()) {
      event.preventDefault();
      return;
    }
  };

  isTextSelected = (): boolean => {
    const input = this.inputBox.nativeElement;
    return input.selectionStart !== input.selectionEnd;
  };

  // Date filter function to prevent past dates based on config type
  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;

    const config = this.config();
    let minDate: Date;

    if (config.type === 'task') {
      // For tasks, disable dates before the start date or today (whichever is later)
      const startDate = this.itemDetails()?.plannedStartDate;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate) {
        const taskStartDate = new Date(startDate);
        taskStartDate.setHours(0, 0, 0, 0);
        // Use the later of start date or today
        minDate = taskStartDate > today ? taskStartDate : today;
      } else {
        // Fallback to today if no start date is available
        minDate = today;
      }
    } else {
      // For issues, revisions, projects, requests, etc., disable dates before today
      minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
    }

    return date >= minDate;
  };

  openPicker = () => {
    this.picker.open();
  };

  /** Open date picker when user clicks anywhere on the Due on card (not only the edit icon). */
  onDueDateCardClick(): void {
    if (this.childExist() || this.isStatusComplete() || this.isUpdatingDate()) {
      return;
    }
    this.openPicker();
  }

 onDateChange = (event: any) => {
    this.selectedDate.set(event.value);
    if (this.itemId() && this.selectedDate()) {
      // Format date as YYYY-MM-DD to avoid timezone issues
      const date = this.selectedDate();
      if (date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        
        const params = {
          [this.getItemIdField()]: this.itemId(),
          projectedCompletionDate: formattedDate,
          plannedCompletionDate: formattedDate,
        };
        
        // Set loading state
        this.isUpdatingDate.set(true);
        this.updateItem(params);
      }
    }
  };

  toggleDiv = () => {
    this.showDiv.set(!this.showDiv());
  };

  prevStep = () => {
    const currentStart = this.visibleStepStart;
    if (currentStart > 0) {
      this._visibleStepStart.set(currentStart - 1);
    }
  };

  nextStep = () => {
    const steps = this.steps();
    const currentStart = this.visibleStepStart;
    const maxStart = Math.max(0, steps.length - 3);
    if (currentStart < maxStart) {
      this._visibleStepStart.set(currentStart + 1);
    }
  };

  updateVisibleSteps = () => {
    const steps = this.steps();
    if (steps.length <= 3) {
      this._visibleStepStart.set(0);
      return;
    }
    // Center the view around the active step
    if (this.activeStep <= 1) {
      this._visibleStepStart.set(0);
    } else if (this.activeStep >= steps.length - 2) {
      this._visibleStepStart.set(steps.length - 3);
    } else {
      this._visibleStepStart.set(this.activeStep - 1);
    }
  };

  // Helper method to get the correct ID field based on config type
  getItemIdField(): string {
    const config = this.config();
    switch (config.type) {
      case 'task':
        return 'taskID';
      case 'issue':
        return 'issueID';
      case 'project':
        return 'projectID';
      case 'revision':
        return 'revisionID';
      case 'request':
        return 'requestID';
      default:
        return 'itemID';
    }
  }

  getItemIdFieldCode(): string {
    const config = this.config();
    switch (config.type) {
      case 'task':
        return 'TASK';
      case 'issue':
        return 'ISSUE';
      case 'project':
        return 'PROJECT';
      case 'revision':
        return 'REVISION';
      case 'request':
        return 'REQUEST';
      default:
        return 'ITEM';
    }
  }

  updateItemAsCompleted(): void {
    if (this.isUpdatingAsCompleted() || !this.showUpdateAsCompletedButton()) {
      return;
    }

    const objID = this.itemId();
    if (!objID) {
      this.toastr.error('Unable to update status as completed.');
      return;
    }

    const objCode = this.getItemIdFieldCode();
    this.isUpdatingAsCompleted.set(true);

    this.apiService.updateAsCompleted({ objCode, objID }).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message || 'Updated as completed successfully.');
        this.clearItemDetailsCache();
        this.getItemDetails(objID);
        this.getApprovalTracking(objID);
        this.statusSidebarRefreshService.triggerRefresh();
        this.isUpdatingAsCompleted.set(false);
      },
      error: (err: any) => {
        this.toastr.error(
          err?.error?.message || err?.message || 'Failed to update as completed.'
        );
        this.isUpdatingAsCompleted.set(false);
      },
    });
  }

  // Helper method to get the correct status field based on config type
  getStatusField(): string {
    const config = this.config();
    switch (config.type) {
      case 'task':
        return 'taskStatus';
      case 'issue':
        return 'issueStatus';
      case 'project':
        return 'projectStatus';
      case 'revision':
        return 'revisionStatus';
      case 'request':
        return 'requestStatus';
      default:
        return 'status';
    }
  }

  getItemDetails(itemId: any) {
    const service = this.service();
    const method = this.getDetailsMethod();

    this.isLoadingAssignedList.set(true);
    service[method](itemId).subscribe({
      next: (res: any) => {
        this.itemDetails.set(res?.data);
         const plannedHourLog = res?.data?.plannedHourLog;
         if (plannedHourLog && Array.isArray(plannedHourLog) && plannedHourLog.length > 0) {
          this.plannedHoursHistory.set(plannedHourLog.map((log: any) => ({
            changedOn: log.createdAt || log.updatedAt,
            changedBy: log.updatedBy?.name || log.updatedBy?.email || 'Unknown',
            oldValue: log.previousPlannedHours || '0',
            newValue: log.currentPlannedHours || '0'
          })));
        } else {
          this.plannedHoursHistory.set([]);
        }
        this.assignedUsers.set(
          (res?.data?.assignees || []).map((assignee: any) => ({
            _id: assignee._id,
            name:
              assignee.name ||
              assignee.jobrole ||
              assignee.teamName ||
              'Unknown',
            image:
              assignee.image || assignee.avatar || 'assets/images/avatar.png',
            assignType: assignee.assignType,
            assignmentStatus: assignee.assignmentStatus,
          }))
        );
        this.syncAssignedListExpand();
        this.selectedDate.set(
          res?.data?.plannedCompletionDate
            ? new Date(res?.data?.plannedCompletionDate)
            : null
        );
        this.submittedValue.set(res?.data?.plannedHours);
        this.originalPlannedHours.set(res?.data?.plannedHours);
        const aiHoursStr = this.formatAiHoursValue(res?.data?.aiHours);
        if (!this.aiHoursLogHasLoaded()) {
          this.aiHoursValue.set(aiHoursStr);
          this.originalAiHours.set(aiHoursStr);
        } else if (this.aiHoursLogTableRows().length > 0) {
          this.applyAiHoursDisplayFromLog();
        } else {
          this.aiHoursValue.set(aiHoursStr);
          this.originalAiHours.set(aiHoursStr);
        }
        
        // Load AI hours log when item details are loaded
        this.loadAiHoursLogOnLoad(res?.data);
        
        this.childExist.set(res?.data?.childExist);
        this.selectedStatus.set(
          res?.data?.taskStatusData?._id ||
            res?.data?.issueStatusData?._id ||
            res?.data?.projectStatusData?._id ||
            res?.data?.revisionStatusData?._id ||
            res?.data?.requestStatusData?._id
        );
         const statusList = res?.data?.taskStatusData?._id || [];
          // Share the pathStartStatusList with other components via ApprovalRefreshService
          this.approvalRefreshService.updatePathStartStatusList(statusList);
        this.selectedApproval.set(res?.data?.approvalProcess);
        this.selectedStatusValue.set(
          res?.data?.taskStatusData?.statusName ||
            res?.data?.issueStatusData?.statusName ||
            res?.data?.projectStatusData?.statusName ||
            res?.data?.revisionStatusData?.statusName ||
            res?.data?.requestStatusData?.statusName ||
            null
        );
        this.selectedStatusApproval.set(res?.data?.statusApproval);
        this.updateStatusListApproval();
        this.getApprovalSetStatus();

        // Extract percentageComplete from API response and share with sidebar
        const objCode = this.getItemIdFieldCode();
        const currentItemId = this.itemId();
        if (currentItemId) {
          const parsedPercentage = parsePercentageComplete(res?.data?.percentageComplete);
          if (parsedPercentage !== null) {
            this.projectHoursService.setPercentageComplete(
              currentItemId,
              objCode,
              parsedPercentage
            );
          } else {
            this.projectHoursService.clearPercentageComplete(currentItemId, objCode);
          }
        }

        // Calculate task percentage for time summary (fallback if percentageComplete not available)
        this.calculateTaskPercentage(res?.data);

        const itemData = res?.data;
        if (objCode === 'REQUEST') {
          this.requestCreatedBy.set(itemData?.createdBy ?? null);
        }else if (objCode === 'ISSUE') {
          this.requestCreatedBy.set(itemData?.createdBy ?? null);
        }else if (objCode === 'TASK') {
          this.requestCreatedBy.set(itemData?.createdBy ?? null);
        }else if (objCode === 'REVISION') {
          this.requestCreatedBy.set(itemData?.createdBy ?? null);
        }
         else {
          this.requestCreatedBy.set(null);
        }
        const owner =
          itemData?.projectOwner ??
          itemData?.projectData?.projectOwner ??
          itemData?.project?.projectOwner ??
          null;
        this.projectOwner.set(owner?._id && owner?.name ? owner : null);

        // Call getSingleApproval if approvalProcess exists
        if (res?.data?.approvalProcess) {
          this.getSingleApproval(res.data.approvalProcess);
        } else {
          // If no approvalProcess, set approvalExists to false
          this.approvalExists.set(false);
          this.singleApprovalData.set(null);
          this.displayApprovalRejectButton.set(false);
          this.approverID.set(null);
        }
        this.isLoadingAssignedList.set(false);
      },
      error: (err: any) => {
        console.error(err);
        this.isLoadingAssignedList.set(false);
      },
    });
  }

  getAssignList() {
    this.isLoadingData.set(true);
    this.apiService.getAssignList().subscribe({
      next: (res: any) => {
        this.allAssignableData.set(
          (res?.data || []).map((item: any) => {
            const normalizedItem = {
              ...item,
              name: item.name || item.jobrole || item.teamName || 'Unknown',
              image: item.image || item.avatar || 'assets/images/avatar.png',
            };
            return normalizedItem;
          })
        );
        this.isLoadingData.set(false);
      },
      error: (err: any) => {
        console.error(err);
        this.isLoadingData.set(false);
      },
    });
  }

  getStatus() {
    const config = this.config();
    const objCode = config.type.toUpperCase();

    this.apiService.getStatus({ objCode }).subscribe({
      next: (res: any) => {
        this.statusList.set(
          res?.data?.map((item: any) => ({
            label: item.statusName,
            value: item._id,
            color: item.color,
          }))
        );
        this.updateStatusListApproval();
      },
      error: (err: any) => {
        console.error(err);
      },
    });
  }

  getApprovalSetStatus() {
    this.apiService
      .getApprovalExistingStatuses(this.itemId(), this.getItemIdFieldCode())
      .subscribe({
        next: (res: any) => {
          this.pathStartStatusList.set(res?.data?.pathStartStatusList || []);
        },
        error: (err: any) => {
          console.error(err);
        },
      });
  }

  updateStatusListApproval = () => {
    this.statusListApproval.set(
      this.statusList().filter(
        (item: any) => item.value !== this.selectedStatus()
      )
    );
  };

  // Computed signal for status list with 'Pending Approval' label
  statusListWithApproval = computed(() => {
    const pendingList = this.pathStartStatusList();
    const currentStatusValue = this.selectedStatusValue();
    const isCurrentlyNew = currentStatusValue?.toLowerCase() === 'new';
    
    // Filter out "New" status if current status is not "New"
    let filteredList = this.statusList();
    if (!isCurrentlyNew) {
      filteredList = filteredList.filter((item) => 
        item.label?.toLowerCase() !== 'new'
      );
    }
    
    return filteredList.map((item) => ({
      ...item,
      label: pendingList.includes(item.value)
        ? `${item.label} - Pending Approval`
        : item.label,
    }));
  });

  getApprovalTracking(itemId: any) {
    this.projectsService
      .getApprovalTracking(itemId, this.getItemIdFieldCode())
      .subscribe({
        next: (res: any) => {
          this.userPreference.set(res?.data?.userPreference);
          this.approvalPaths.set(res?.data?.approvalPaths[0] || []);
          this.displayApprovalRejectButton.set(false);
          this.approverID.set(null);

          if (this.approvalPaths()?.stages?.length > 0) {
            this.checkShowApprovalButton(res?.data);
          }

          this.steps.set(
            this.approvalPaths()?.stages?.map((item: any) => ({
              label: item.stageName,
              status: item.status,
            })) || []
          );
          let lastApprovedIndex = -1;
          for (let i = 0; i < this.steps()?.length; i++) {
            if (this.steps()[i]?.status === 'approved') {
              lastApprovedIndex = i;
            }
          }
          this._activeStep.set(
            lastApprovedIndex !== -1 ? lastApprovedIndex + 1 : 0
          );
          if (this.activeStep >= this.steps()?.length) {
            this._activeStep.set(this.steps()?.length - 1);
          }
          this.updateVisibleSteps();
        },
        error: (err: any) => {
          console.error(err);
          this.displayApprovalRejectButton.set(false);
          this.approverID.set(null);
        },
      });
  }

  private getCurrentApprovalPathAndStage(trackingData?: any): {
    currentPath: number | string;
    currentStage: number | string;
  } {
    const singleApproval = this.singleApprovalData();
    const approvalPaths = this.approvalPaths();
    const requestedStage = approvalPaths?.stages?.find(
      (stage: any) => stage.status === 'requested'
    );

    return {
      currentPath:
        trackingData?.currentPath ??
        singleApproval?.currentPath ??
        approvalPaths?.path ??
        1,
      currentStage:
        trackingData?.currentStage ??
        singleApproval?.currentStage ??
        requestedStage?.stageNumber ??
        1,
    };
  }

  private checkShowApprovalButton(trackingData?: any): void {
    const singleApproval = this.singleApprovalData();

    const approvalID =
      this.selectedApproval() ||
      trackingData?.approvalID ||
      trackingData?._id ||
      singleApproval?._id;

    if (!approvalID) {
      return;
    }

    const { currentPath, currentStage } =
      this.getCurrentApprovalPathAndStage(trackingData);

    this.approvalService
      .showApprovalButton({
        approvalID,
        currentPath,
        currentStage,
      })
      .subscribe({
        next: (res: any) => {
          const shouldShow = !!(res?.showApproveButton ?? res?.data?.showApproveButton);
          this.displayApprovalRejectButton.set(shouldShow);

          if (shouldShow) {
            this.setApproverIdFromMatchedAs(res?.matchedAs ?? res?.data?.matchedAs);
          } else {
            this.approverID.set(null);
          }
        },
        error: (err: any) => {
          console.error('Error checking show approval button:', err);
          this.displayApprovalRejectButton.set(false);
          this.approverID.set(null);
        },
      });
  }

  private setApproverIdFromMatchedAs(matchedAs?: string): void {
    const normalized = (matchedAs || '').toUpperCase();
    const userID = localStorage.getItem('userID');
    const jobRoleID = localStorage.getItem('jobRoleID');
    const homeTeamID = localStorage.getItem('homeTeamID');
    this.approverID.set(userID);
  }
    openApproveConfirmPopup(): void {
    this.showApproveConfirmPopup.set(true);
  }

  cancelApproveConfirmPopup(): void {
    if (this.isApprovingApproval()) {
      return;
    }
    this.showApproveConfirmPopup.set(false);
  }

  openRejectConfirmPopup(): void {
    this.showRejectConfirmPopup.set(true);
  }

  cancelRejectConfirmPopup(): void {
    if (this.isRejectingApproval()) {
      return;
    }
    this.showRejectConfirmPopup.set(false);
  }

  private isApprovalActionFailed(res: any): boolean {
    if (res?.__error === true || res?.success === false) {
      return true;
    }
    return !(res?.success === true || res?.statusCode === 200 || res?.statusCode === 304);
  }

  private getApprovalActionErrorMessage(res: any, fallback: string): string {
    return (
      res?.error?.message ||
      res?.err?.error?.message ||
      res?.message ||
      res?.err?.message ||
      fallback
    );
  }

  private emitApprovalActionCompleted(action: 'approve' | 'reject'): void {
    const objID = this.itemId();
    const objCode = this.getItemIdFieldCode();
    if (!objID) {
      return;
    }

    this.crossTabSyncService.sendSyncEvent({
      type: 'approval_action_completed',
      taskId: objCode === 'TASK' ? objID : undefined,
      issueId: objCode === 'ISSUE' ? objID : undefined,
      requestId: objCode === 'REQUEST' ? objID : undefined,
      revisionId: objCode === 'REVISION' ? objID : undefined,
      data: {
        action,
        objID,
        objCode,
        approvalProcessID: this.selectedApproval(),
      },
      timestamp: Date.now(),
    });
  }

  private refreshApprovalUiAfterAction(): void {
    const id = this.itemId();
    if (id) {
      this.getApprovalTracking(id);
      this.getItemDetails(id);
    }
    this.displayApprovalRejectButton.set(false);
    this.approvalRefreshService.triggerRefresh();
  }

  approveApproval() {

    if (this.isApprovingApproval() || this.isRejectingApproval()) {
      return;
    }
    this.isApprovingApproval.set(true);
    const { currentPath, currentStage } = this.getCurrentApprovalPathAndStage();
    this.approvalService.acceptOrRejectApproval({ 
      approvalProcessID: this.selectedApproval(),
      approverID: this.approverID(),
      action: 'approve',
      objCode: this.getItemIdFieldCode(),
      objID: this.itemId(),
      currentPath,
      currentStage,
    }).subscribe({
      next: (res: any) => {
        if (this.isApprovalActionFailed(res)) {
          this.toastr.error(this.getApprovalActionErrorMessage(res, 'Failed to approve'));
          this.refreshApprovalUiAfterAction();
          this.emitApprovalActionCompleted('approve');
          this.isApprovingApproval.set(false);
          return;
        }
        this.toastr.success(res?.message || 'Approval approved successfully');
        this.refreshApprovalUiAfterAction();
        this.emitApprovalActionCompleted('approve');
        this.isApprovingApproval.set(false);
        this.showApproveConfirmPopup.set(false);
      },
      error: (err: any) => {
        this.toastr.error(
          this.getApprovalActionErrorMessage(err, 'Failed to approve')
        );
        this.isApprovingApproval.set(false);
      },
    });
  }

   rejectApproval(result?: ConfirmPopupResult) {

    if (this.isApprovingApproval() || this.isRejectingApproval()) {
      return;
    }
    this.isRejectingApproval.set(true);

    const reason = (result?.reason || '').trim();
    const objCode = this.getItemIdFieldCode();
    const objID = this.itemId();
    const details = this.itemDetails();

    const { currentPath, currentStage } = this.getCurrentApprovalPathAndStage();
    const reject$ = this.approvalService.acceptOrRejectApproval({
      approvalProcessID: this.selectedApproval(),
      approverID: this.approverID(),
      action: 'reject',
      objCode,
      objID,
      currentPath,
      currentStage,
    }).pipe(
      catchError((err: any) => {
        console.error('Error rejecting approval:', err);
        return of({ __error: true, __source: 'reject', err });
      })
    );

    // Create-update only when reason text exists; both APIs fire in parallel.
    const update$ = reason
      ? this.updateService.addUpdate({
          textHTML: reason,
          objCode,
          objID,
          projectID: this.getRejectUpdateProjectId(details),
          objName: this.getRejectUpdateObjName(details),
          notify: []
        }).pipe(
          catchError((err: any) => {
            console.error('Error creating rejection update:', err);
            return of({ __error: true, __source: 'update', err });
          })
        )
      : of(null);

    forkJoin({ reject: reject$, update: update$ }).subscribe({
      next: ({ reject: res, update: updateRes }: any) => {
        const rejectFailed = this.isApprovalActionFailed(res);
        const updateFailed = !!reason && updateRes?.__error === true;

        if (!rejectFailed) {
          this.toastr.success(res?.message || 'Approval rejected successfully');
          this.refreshApprovalUiAfterAction();
          this.emitApprovalActionCompleted('reject');
        } else {
          this.toastr.error(this.getApprovalActionErrorMessage(res, 'Failed to reject'));
          this.refreshApprovalUiAfterAction();
          this.emitApprovalActionCompleted('reject');
        }

        if (updateFailed) {
          this.toastr.error(
            this.getApprovalActionErrorMessage(updateRes, 'Failed to add rejection reason update')
          );
        }

        this.isRejectingApproval.set(false);
        this.showRejectConfirmPopup.set(false);
      },
      error: (err: any) => {
        console.error('Error rejecting approval:', err);
        this.toastr.error(this.getApprovalActionErrorMessage(err, 'Failed to reject'));
        this.isRejectingApproval.set(false);
      },
    });
  }

  private getRejectUpdateProjectId(details: any): string {
    if (this.config().type === 'project') {
      return this.itemId() || details?._id || '';
    }
    return (
      details?.projectID ||
      details?.projectId ||
      details?.project?._id ||
      details?.project?.id ||
      details?.task?.projectID ||
      details?.issue?.projectID ||
      details?.request?.projectID ||
      details?.revision?.projectID ||
      ''
    );
  }

  private getRejectUpdateObjName(details: any): string {
    return (
      details?.name ||
      details?.taskName ||
      details?.issueName ||
      details?.requestName ||
      details?.revisionName ||
      details?.projectName ||
      details?.title ||
      details?.objName ||
      ''
    );
  }
  

  getApprovals() {
    const config = this.config();
    const objCode = config.type.toUpperCase();

    this.approvalService.getApprovals({ objCode }).subscribe({
      next: (res) => {
        this.approvalList.set(
          res?.data?.map((item: any) => ({
            _id: item._id,
            name: item.approvalName,
            approvalName: item.approvalName,
            approvalDescription: item.approvalDescription,
            approvalPaths: item.approvalPaths || [],
            currentStage: item.currentStage,
            currentPath: item.currentPath,
            status: item.status,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            objCode: item.objCode,
          }))
        );
      },
      error: (err) => {
        console.error('Error loading approvals:', err);
      },
    });
  }

  getSingleApproval(approvalProcessId: string) {
    this.approvalService.getSingleApproval(approvalProcessId).subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.applyAutoFilledApprovers(res.data);
          this.singleApprovalData.set(res.data);
          // Set the selected approval to the single approval data
          this.selectedApproval.set(res.data._id);
          this.approvalExists.set(true);

          // Tracking and single-approval can resolve in either order; re-check when ID/path/stage are ready
          if (this.approvalPaths()?.stages?.length > 0) {
            this.checkShowApprovalButton();
          }
        } else {
          // If success is false or no data, approval doesn't exist
          this.approvalExists.set(false);
          this.singleApprovalData.set(null);
          this.selectedApproval.set(null);
          this.displayApprovalRejectButton.set(false);
          this.approverID.set(null);
        }
      },
      error: (err: any) => {
        console.error('Error loading single approval:', err);
        // On error, assume approval doesn't exist
        this.approvalExists.set(false);
        this.singleApprovalData.set(null);
        this.selectedApproval.set(null);
        this.displayApprovalRejectButton.set(false);
        this.approverID.set(null);
      },
    });
  }

  /**
   * When a stage is named "Send to Requestor" and has no approvers,
   * fill it with the request's createdBy person (REQUEST objCode only).
   */
  private populateSendToRequestorApprovers(approval: any | null): boolean {
    if (!approval?.approvalPaths?.length || this.getItemIdFieldCode() !== 'REQUEST' && this.getItemIdFieldCode() !== 'ISSUE'  && this.getItemIdFieldCode() !== 'TASK'  && this.getItemIdFieldCode() !== 'REVISION') {
      return false;
    }

    const createdBy = this.requestCreatedBy();
    if (!createdBy?._id || !createdBy?.name) {
      return false;
    }

    let changed = false;
    for (const path of approval.approvalPaths) {
      if (!path?.stages?.length) {
        continue;
      }
      for (const stage of path.stages) {
        if ((stage.stageName || '').trim() !== StatusSidebar.SEND_TO_REQUESTOR_STAGE) {
          continue;
        }
        if (stage.approvers?.length) {
          continue;
        }
        stage.approvers = [{
          name: createdBy.name,
          _id: createdBy._id,
          type: 'PEOPLE',
          status: 'pending'
        }];
        changed = true;
      }
    }

    if (changed) {
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
    return changed;
  }

  /**
   * When a stage is named "Send to TWCAN PM/Owner for Approval" and has no approvers,
   * fill it with the current project's projectOwner.
   */
  private populateSendToProjectOwnerApprovers(approval: any | null): boolean {
    if (!approval?.approvalPaths?.length) {
      return false;
    }

    const owner = this.projectOwner();
    if (!owner?._id || !owner?.name) {
      return false;
    }

    let changed = false;
    for (const path of approval.approvalPaths) {
      if (!path?.stages?.length) {
        continue;
      }
      for (const stage of path.stages) {
        if ((stage.stageName || '').trim() !== StatusSidebar.SEND_TO_PROJECT_OWNER_STAGE) {
          continue;
        }
        if (stage.approvers?.length) {
          continue;
        }
        stage.approvers = [{
          name: owner.name,
          _id: owner._id,
          type: 'PEOPLE',
          status: 'pending'
        }];
        changed = true;
      }
    }

    if (changed) {
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
    return changed;
  }

  private applyAutoFilledApprovers(approval: any | null): void {
    if (!approval) {
      return;
    }
    this.populateSendToRequestorApprovers(approval);
    this.populateSendToProjectOwnerApprovers(approval);
  }

  // Load assign list for approver selection
  loadAssignList() {
    this.apiService.getAssignList().subscribe({
      next: (res: any) => {
        this.assignList.set(res?.data || []);
        this.approvalAvailableAssigneesCacheMap.clear();
      },
      error: (err: any) => {
        console.error('Error loading assign list:', err);
        this.assignList.set([]);
        this.approvalAvailableAssigneesCacheMap.clear();
      },
    });
  }

  // Get selected approval details
  getSelectedApprovalDetails(): any | null {
    if (!this.selectedApproval()) return null;

    // First check if we have single approval data (for existing approvals)
    if (this.singleApprovalData()) {
      return this.singleApprovalData();
    }

    // Otherwise, check the approval list (for new approvals)
    const approval = this.approvalList().find(
      (approval) => approval._id === this.selectedApproval()
    );
    return approval || null;
  }

  trackApprovalStageByNumber(_index: number, stage: { stageNumber: number }): number {
    return stage.stageNumber;
  }

  // Path management methods
  updatePathStartStatus(pathIndex: number, newStatus: string) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex]
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      approval.approvalPaths[pathIndex].pathStartStatus = newStatus;
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
      // Auto-hide validation errors if form becomes valid
      if (this.showApprovalValidationErrors()) {
        const validation = this.approvalValidationResult();
        if (validation.isValid) {
          this.showApprovalValidationErrors.set(false);
        }
      }
    }
  }

  updatePathRejectStatus(pathIndex: number, newStatus: string) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex]
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      // If user clears the selection, set to 'previousStatus' as default
      approval.approvalPaths[pathIndex].rejectStatus = newStatus || 'previousStatus';
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
      // Auto-hide validation errors if form becomes valid
      if (this.showApprovalValidationErrors()) {
        const validation = this.approvalValidationResult();
        if (validation.isValid) {
          this.showApprovalValidationErrors.set(false);
        }
      }
    }
  }

  addNewPath() {
    const approval = this.getSelectedApprovalDetails();
    if (approval && approval.approvalPaths) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      const newPathNumber = approval.approvalPaths.length + 1;
      const newPath = {
        path: newPathNumber,
        pathStartStatus: null,
        rejectStatus: 'previousStatus',
        isApproved: false,
        stages: [
          {
            stageNumber: 1,
            stageName: 'New Stage',
            approvers: [],
            isOneDecision: false,
            status: 'pending',
            approvedBy: [],
            rejectedBy: [],
          },
        ],
      };
      approval.approvalPaths.push(newPath);
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  removePath(pathIndex: number) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths.length > 1
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      approval.approvalPaths.splice(pathIndex, 1);
      // Renumber paths
      approval.approvalPaths.forEach((path: any, index: number) => {
        path.path = index + 1;
      });
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  // Stage management methods
  updateStageName(pathIndex: number, stageIndex: number, newName: string) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages[stageIndex]
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      approval.approvalPaths[pathIndex].stages[stageIndex].stageName = newName;
      this.applyAutoFilledApprovers(approval);
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
      // Auto-hide validation errors if form becomes valid
      if (this.showApprovalValidationErrors()) {
        const validation = this.approvalValidationResult();
        if (validation.isValid) {
          this.showApprovalValidationErrors.set(false);
        }
      }
    }
  }

  updateStageOneDecision(pathIndex: number, stageIndex: number, event: any) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages[stageIndex]
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      approval.approvalPaths[pathIndex].stages[stageIndex].isOneDecision =
        event.target.checked;
    }
  }

  addNewStage(pathIndex: number) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex]
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      const path = approval.approvalPaths[pathIndex];
      const newStageNumber = path.stages.length + 1;
      const newStage = {
        stageNumber: newStageNumber,
        stageName: 'New Stage',
        approvers: [],
        isOneDecision: false,
        status: 'pending',
        approvedBy: [],
        rejectedBy: [],
      };
      path.stages.push(newStage);
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  openApprovalStageRemoveConfirm(pathIndex: number, stageIndex: number): void {
    const approval = this.getSelectedApprovalDetails();
    const path = approval?.approvalPaths?.[pathIndex];
    const stage = path?.stages?.[stageIndex];
    if (!path || !stage || path.stages.length <= 1) {
      return;
    }
    const stageName = (stage.stageName || '').trim() || `Stage ${stage.stageNumber}`;
    this.pendingApprovalStageRemoval.set({ pathIndex, stageIndex, stageName });
    this.showApprovalRemoveStageConfirmPopup.set(true);
  }

  confirmRemoveApprovalStage(): void {
    const p = this.pendingApprovalStageRemoval();
    if (!p) {
      this.showApprovalRemoveStageConfirmPopup.set(false);
      return;
    }
    this.removeStage(p.pathIndex, p.stageIndex);
    this.pendingApprovalStageRemoval.set(null);
    this.showApprovalRemoveStageConfirmPopup.set(false);
  }

  cancelRemoveApprovalStage(): void {
    this.pendingApprovalStageRemoval.set(null);
    this.showApprovalRemoveStageConfirmPopup.set(false);
  }

  removeStage(pathIndex: number, stageIndex: number) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages.length > 1
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      approval.approvalPaths[pathIndex].stages.splice(stageIndex, 1);
      // Renumber stages
      approval.approvalPaths[pathIndex].stages.forEach(
        (stage: any, index: number) => {
          stage.stageNumber = index + 1;
        }
      );
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
    }
  }

  private normalizeAssigneePrimitive(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      const t = value.trim();
      return t === '' || t === 'undefined' || t === 'null' ? '' : t;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    if (typeof value === 'object') {
      const o = value as Record<string, unknown>;
      if (typeof o['$oid'] === 'string') {
        return o['$oid'];
      }
    }
    return '';
  }

  private getAssigneeRecordId(
    record: Record<string, unknown> | null | undefined
  ): string {
    if (!record) {
      return '';
    }
    return (
      this.normalizeAssigneePrimitive(record['_id']) ||
      this.normalizeAssigneePrimitive(record['id']) ||
      this.normalizeAssigneePrimitive(record['userId']) ||
      this.normalizeAssigneePrimitive(
        (record['user'] as Record<string, unknown> | undefined)?.['_id']
      ) ||
      ''
    );
  }

  private resolveSelectedAssigneeId(assigneeId: unknown): string {
    if (assigneeId == null || assigneeId === '') {
      return '';
    }
    const fromPrim = this.normalizeAssigneePrimitive(assigneeId);
    if (fromPrim) {
      return fromPrim;
    }
    if (typeof assigneeId === 'object') {
      return this.getAssigneeRecordId(assigneeId as Record<string, unknown>);
    }
    return String(assigneeId).trim();
  }

  /** Assignees not yet added to this approval stage (ng-select items). Memoized. */
  getAvailableAssigneesForApprovalStage(pathIndex: number, stageIndex: number): any[] {
    const list = this.assignList() || [];
    const approval = this.getSelectedApprovalDetails();
    const approvers =
      approval?.approvalPaths?.[pathIndex]?.stages?.[stageIndex]?.approvers ?? [];
    const takenIds = approvers
      .map((ap: { _id?: string; id?: string }) =>
        this.getAssigneeRecordId(ap as unknown as Record<string, unknown>)
      )
      .filter((id: string) => id.length > 0);
    const taken = new Set(takenIds);
    const fp = `${approvers.length}:${[...taken].sort().join('\u001f')}`;
    const cacheKey = `${this.approvalDataChangeTrigger()}|${pathIndex}|${stageIndex}|${list.length}|${fp}`;
    const cached = this.approvalAvailableAssigneesCacheMap.get(cacheKey);
    if (cached) {
      return cached;
    }
    const items = list.filter((u) => {
      const id = this.getAssigneeRecordId(u as Record<string, unknown>);
      if (!id) {
        return true;
      }
      return !taken.has(id);
    });
    this.approvalAvailableAssigneesCacheMap.set(cacheKey, items);
    return items;
  }

  // Approver management methods
  addApprover(
    pathIndex: number,
    stageIndex: number,
    assigneeId: string | null,
    selectRef?: any
  ) {
    if (assigneeId == null || assigneeId === '') {
      return;
    }
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages[stageIndex]
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }

      const stage = approval.approvalPaths[pathIndex].stages[stageIndex];
      const idStr = this.resolveSelectedAssigneeId(assigneeId);
      if (
        idStr &&
        stage.approvers.some(
          (a: { _id?: string; id?: string }) =>
            this.getAssigneeRecordId(a as unknown as Record<string, unknown>) ===
            idStr
        )
      ) {
        if (selectRef) {
          selectRef.clearModel();
        }
        this.toastr.info('This approver is already added to this stage.');
        return;
      }

      const assignee = this.assignList().find((item) => {
        const rowId = this.getAssigneeRecordId(item as Record<string, unknown>);
        return rowId ? rowId === idStr : item._id === assigneeId || item === assigneeId;
      });
      if (assignee) {
        const newApprover = {
          name: assignee.name,
          _id: assignee._id,
          type: assignee.assignType || 'PEOPLE',
          status: 'pending'
        };
        approval.approvalPaths[pathIndex].stages[stageIndex].approvers.push(
          newApprover
        );
        // Trigger recomputation of validation
        this.approvalDataChangeTrigger.update(v => v + 1);
        // Auto-hide validation errors if form becomes valid
        if (this.showApprovalValidationErrors()) {
          const validation = this.approvalValidationResult();
          if (validation.isValid) {
            this.showApprovalValidationErrors.set(false);
          }
        }

        // Clear dropdown after selection
        if (selectRef) {
          selectRef.clearModel();
        }
      }
    }
  }

  removeApprover(pathIndex: number, stageIndex: number, approverIndex: number) {
    const approval = this.getSelectedApprovalDetails();
    if (
      approval &&
      approval.approvalPaths &&
      approval.approvalPaths[pathIndex] &&
      approval.approvalPaths[pathIndex].stages &&
      approval.approvalPaths[pathIndex].stages[stageIndex]
    ) {
      // Create backup if not exists (for subsequent edits after cancel)
      if (!this.originalApprovalData()) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(approval)));
      }
      approval.approvalPaths[pathIndex].stages[stageIndex].approvers.splice(
        approverIndex,
        1
      );
      // Trigger recomputation of validation
      this.approvalDataChangeTrigger.update(v => v + 1);
      // Auto-hide validation errors if form becomes valid
      if (this.showApprovalValidationErrors()) {
        const validation = this.approvalValidationResult();
        if (validation.isValid) {
          this.showApprovalValidationErrors.set(false);
        }
      }
    }
  }

  updateItem(params: any, isAssigneeRemoval: boolean = false, isAssigneeAssignment: boolean = false) {
    const service = this.service();
    const method = this.updateMethod();
    const isAssigneeUpdate = params.assignees || params.removedAssignees;
    service[method](params).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message);
        // Clear the cache for this item so that getItemDetails fetches fresh data
        if (service.clearSingleTaskCache) {
          service.clearSingleTaskCache(this.itemId());
        }
        if (service.clearSingleIssueCache) {
          service.clearSingleIssueCache(this.itemId());
        }
        if (service.clearSingleProjectCache) {
          service.clearSingleProjectCache(this.itemId());
        }
        if (service.clearSingleRevisionCache) {
          service.clearSingleRevisionCache(this.itemId());
        }
        if (service.clearSingleRequestCache) {
          service.clearSingleRequestCache(this.itemId());
        }
        this.getItemDetails(this.itemId());
        const isStatusUpdate = params[this.getStatusField()];
        const isPlannedHoursUpdate = params.plannedHours !== undefined;
        if (isStatusUpdate) {
          this.isUpdatingStatus.set(false);
          this.getApprovalTracking(this.itemId());
          // Trigger refresh in task details component to update isStatusComplete immediately
          this.statusSidebarRefreshService.triggerRefresh();
          
          // Trigger cross-tab sync and project data refresh for task and revision status updates
          const config = this.config();
          if (config.type === 'task') {
            // Try to get projectId from multiple sources:
            // 1. Response data (if update response includes task data)
            // 2. Current itemDetails (might have old data but should have projectID)
            // 3. Route params (for tasks within a project)
            const projectId = res?.data?.projectID || 
                             res?.data?.task?.projectID ||
                             res?.data?.projectId ||
                             this.itemDetails()?.projectID || 
                             this.itemDetails()?.projectId ||
                             this.route.snapshot?.parent?.params?.['id'] ||
                             this.route.snapshot?.params?.['id'];
            
            if (projectId) {
              // Send cross-tab sync event
              const statusId = params[this.getStatusField()];
              const statusOption = this.statusList().find((s: any) => s.value === statusId);
              const statusName = statusOption?.label ?? this.selectedStatusValue() ?? 'Not Selected';
              const statusColor = statusOption?.color ?? '#979BA8';
              const syncEvent = {
                type: 'task_status_updated',
                projectId: projectId,
                taskId: this.itemId() || undefined,
                data: {
                  projectId: projectId,
                  taskId: this.itemId(),
                  // Used as quick fallback by listeners
                  status: statusId,
                  // Used by task-list display logic (row.taskStatus)
                  taskStatus: statusId,
                  // Used by task-list to update the Status column without a full reload
                  taskStatusData: {
                    _id: statusId,
                    statusName,
                    color: statusColor
                  },
                  timestamp: Date.now()
                },
                timestamp: Date.now()
              };
              this.crossTabSyncService.sendSyncEvent(syncEvent);
              
              // Trigger project data refresh to update project-status charts
              this.projectsService.triggerProjectDataRefresh(projectId);
            } else {
              // Still trigger the refresh signal so task listing pages update in the same tab.
              // (Cross-tab syncing depends on projectId, but same-tab uses the refresh signal.)
              this.projectsService.triggerProjectDataRefresh();
              
            }
          } else if (config.type === 'revision') {
            // Try to get projectId from multiple sources for revisions
            const projectId = res?.data?.projectID || 
                             res?.data?.revision?.projectID ||
                             res?.data?.projectId ||
                             this.itemDetails()?.projectID || 
                             this.itemDetails()?.projectId ||
                             this.route.snapshot?.parent?.params?.['id'] ||
                             this.route.snapshot?.params?.['id'];
            
            if (projectId) {
              // Send cross-tab sync event for revision status update
              // Include revision data from response if available to avoid unnecessary API calls
              const revisionData = res?.data?.revision || res?.data;
              const syncEvent = {
                type: 'revision_status_updated',
                projectId: projectId,
                revisionId: this.itemId() || undefined,
                data: {
                  projectId: projectId,
                  revisionId: this.itemId(),
                  status: params[this.getStatusField()],
                  revision: revisionData, // Include full revision data if available
                  timestamp: Date.now()
                },
                timestamp: Date.now()
              };
              this.crossTabSyncService.sendSyncEvent(syncEvent);
            } else {
              console.warn('Status-sidebar: Could not determine projectId for revision status update. Available data:', {
                responseData: res?.data,
                itemDetails: this.itemDetails(),
                routeParams: this.route.snapshot?.params,
                parentRouteParams: this.route.snapshot?.parent?.params
              });
            }
          } else if (config.type === 'request') {
            // Try to get projectId from multiple sources for requests
            const projectId = res?.data?.projectID || 
                             res?.data?.request?.projectID ||
                             res?.data?.projectId ||
                             this.itemDetails()?.projectID || 
                             this.itemDetails()?.projectId ||
                             this.route.snapshot?.parent?.params?.['id'] ||
                             this.route.snapshot?.params?.['id'];
            
            if (projectId) {
              // Send cross-tab sync event for request status update
              // Include request data from response if available to avoid unnecessary API calls
              const requestData = res?.data?.request || res?.data;
              const syncEvent = {
                type: 'request_status_updated',
                projectId: projectId,
                requestId: this.itemId() || undefined,
                data: {
                  projectId: projectId,
                  requestId: this.itemId(),
                  status: params[this.getStatusField()],
                  request: requestData, // Include full request data if available
                  timestamp: Date.now()
                },
                timestamp: Date.now()
              };
              this.crossTabSyncService.sendSyncEvent(syncEvent);
            } else {
              
            }
          } else if (config.type === 'issue') {
            // Try to get projectId from multiple sources for issues
            const projectId = res?.data?.projectID || 
                             res?.data?.issue?.projectID ||
                             res?.data?.projectId ||
                             this.itemDetails()?.projectID || 
                             this.itemDetails()?.projectId ||
                             this.route.snapshot?.parent?.params?.['id'] ||
                             this.route.snapshot?.params?.['id'];
            
            if (projectId) {
              // Send cross-tab sync event for issue status update
              // Include issue data from response if available to avoid unnecessary API calls
              const issueData = res?.data?.issue || res?.data;
              const statusId = params[this.getStatusField()];
              const statusOption = this.statusList().find((s: any) => s.value === statusId);
              const statusName = statusOption?.label ?? this.selectedStatusValue() ?? 'Not Selected';
              const statusColor = statusOption?.color ?? '#979BA8';
              const syncEvent = {
                type: 'issue_status_updated',
                projectId: projectId,
                issueId: this.itemId() || undefined,
                data: {
                  projectId: projectId,
                  issueId: this.itemId(),
                  status: statusId,
                  issue: issueData, // Include full issue data if available
                  issueStatusData: {
                    _id: statusId,
                    statusName,
                    color: statusColor
                  },
                  timestamp: Date.now()
                },
                timestamp: Date.now()
              };
              this.crossTabSyncService.sendSyncEvent(syncEvent);
            } else {
             
            }
          }
        }

        // Task (task-detail / task-status sidebar): broadcast non-status field updates so task-listing refreshes in other tabs
        if (!isStatusUpdate && this.config().type === 'task') {
          const projectIdForSync =
            res?.data?.projectID ||
            res?.data?.task?.projectID ||
            res?.data?.projectId ||
            this.itemDetails()?.projectID ||
            this.itemDetails()?.projectId ||
            this.route.snapshot?.parent?.params?.['id'] ||
            this.route.snapshot?.params?.['id'];
          const tid = this.itemId();
          if (projectIdForSync && tid) {
            const taskPayload = res?.data?.task || res?.data || this.itemDetails();
            this.crossTabSyncService.sendSyncEvent({
              type: 'task_field_updated',
              projectId: projectIdForSync,
              taskId: tid,
              data: {
                projectId: projectIdForSync,
                taskId: tid,
                task: taskPayload,
                timestamp: Date.now()
              },
              timestamp: Date.now()
            });
          }
        }

        if (isPlannedHoursUpdate) {
          const config = this.config();
          if (config.type === 'task') {
            // Try to get projectId from multiple sources for tasks
            const projectId = res?.data?.projectID || 
                             res?.data?.task?.projectID ||
                             res?.data?.projectId ||
                             this.itemDetails()?.projectID || 
                             this.itemDetails()?.projectId ||
                             this.route.snapshot?.parent?.params?.['id'] ||
                             this.route.snapshot?.params?.['id'];
            
            if (projectId) {
              // Trigger project data refresh to update project-status charts
              this.projectsService.triggerProjectDataRefresh(projectId);
            } else {
              // Still trigger the refresh signal so project listing pages update in the same tab
              this.projectsService.triggerProjectDataRefresh();
              
            }
          } else if (config.type === 'request') {
            // Refresh submenu counts for requests when planned hours are updated
            const currentItemId = this.itemId();
            const objCode = this.getItemIdFieldCode();
            if (currentItemId) {
              this.submenuCountService.refreshProjectHours(currentItemId, objCode);
            }
          } else if (config.type === 'issue') {
            // Refresh submenu counts for issues when planned hours are updated
            const currentItemId = this.itemId();
            const objCode = this.getItemIdFieldCode();
            if (currentItemId) {
              this.submenuCountService.refreshProjectHours(currentItemId, objCode);
            }
          } else if (config.type === 'revision') {
            // Refresh submenu counts for revisions when planned hours are updated
            const currentItemId = this.itemId();
            const objCode = this.getItemIdFieldCode();
            if (currentItemId) {
              this.submenuCountService.refreshProjectHours(currentItemId, objCode);
            }
          }
        }

        // Request (request-detail / request-status sidebar): broadcast field updates so request-listing refreshes in other tabs
        if (!isStatusUpdate && this.config().type === 'request') {
          const projectIdForSync =
            res?.data?.projectID ||
            res?.data?.request?.projectID ||
            res?.data?.projectId ||
            this.itemDetails()?.projectID ||
            this.itemDetails()?.projectId ||
            this.route.snapshot?.parent?.params?.['id'] ||
            this.route.snapshot?.params?.['id'];
          const requestId = this.itemId();
          if (projectIdForSync && requestId) {
            const requestPayload = res?.data?.request || res?.data || this.itemDetails();
            this.crossTabSyncService.sendSyncEvent({
              type: 'request_field_updated',
              projectId: projectIdForSync,
              requestId: requestId,
              data: {
                projectId: projectIdForSync,
                requestId: requestId,
                request: requestPayload,
                timestamp: Date.now()
              },
              timestamp: Date.now()
            });
          }
        }

        // Revision (revision-detail / revision-status sidebar): broadcast field updates so revision-listing refreshes in other tabs
        if (!isStatusUpdate && this.config().type === 'revision') {
          const projectIdForSync =
            res?.data?.projectID ||
            res?.data?.revision?.projectID ||
            res?.data?.projectId ||
            this.itemDetails()?.projectID ||
            this.itemDetails()?.projectId ||
            this.route.snapshot?.parent?.params?.['id'] ||
            this.route.snapshot?.params?.['id'];
          const revisionId = this.itemId();
          if (projectIdForSync && revisionId) {
            const revisionPayload = res?.data?.revision || res?.data || this.itemDetails();
            this.crossTabSyncService.sendSyncEvent({
              type: 'revision_field_updated',
              projectId: projectIdForSync,
              revisionId: revisionId,
              data: {
                projectId: projectIdForSync,
                revisionId: revisionId,
                revision: revisionPayload,
                timestamp: Date.now()
              },
              timestamp: Date.now()
            });
          }
        }

        // Issue (issue-detail / issue-status sidebar): broadcast field updates so issue-listing refreshes in other tabs
        if (!isStatusUpdate && this.config().type === 'issue') {
          const projectIdForSync =
            res?.data?.projectID ||
            res?.data?.issue?.projectID ||
            res?.data?.projectId ||
            this.itemDetails()?.projectID ||
            this.itemDetails()?.projectId ||
            this.route.snapshot?.parent?.params?.['id'] ||
            this.route.snapshot?.params?.['id'];
          const issueId = this.itemId();
          if (projectIdForSync && issueId) {
            const issuePayload = res?.data?.issue || res?.data || this.itemDetails();
            this.crossTabSyncService.sendSyncEvent({
              type: 'issue_field_updated',
              projectId: projectIdForSync,
              issueId: issueId,
              data: {
                projectId: projectIdForSync,
                issueId: issueId,
                issue: issuePayload,
                timestamp: Date.now()
              },
              timestamp: Date.now()
            });
          }
        }

        // Reset planned hours loading state if this was a planned hours update
        if (params.plannedHours !== undefined) {
          this.isUpdatingPlannedHours.set(false);
        }
        // Reset AI hours loading state if this was an AI hours update
        if (params.aiHours !== undefined) {
          this.isUpdatingAiHours.set(false);
        }
        // Reset date loading state if this was a date update
        if (params.plannedCompletionDate !== undefined) {
          this.isUpdatingDate.set(false);
        }
        // If this was an assignee update, trigger refresh in task details component
        if (isAssigneeUpdate) {
          this.statusSidebarRefreshService.triggerRefresh();
        }
        // If this was an assignee removal, close the popup and reset loading state
        if (isAssigneeRemoval) {
          this.isRemovingAssignee.set(false);
          this.showRemoveConfirmPopup.set(false);
          this.userToRemove.set(null);
        }
        // If this was an assignee assignment, reset loading state
        if (isAssigneeAssignment) {
          this.isAssigningUsers.set(false);
          this.closeAssignExpand();
          this.closeAssignModal();
        }
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message);
        // Reset planned hours loading state on error
        if (params.plannedHours !== undefined) {
          this.isUpdatingPlannedHours.set(false);
        }
        // Reset AI hours loading state on error
        if (params.aiHours !== undefined) {
          this.isUpdatingAiHours.set(false);
        }
        // Reset date loading state on error
        if (params.plannedCompletionDate !== undefined) {
          this.isUpdatingDate.set(false);
        }
        // Reset status loading state on error
        if (params[this.getStatusField()]) {
          this.isUpdatingStatus.set(false);
        }
        // Reset assignee removal loading state on error
        if (isAssigneeRemoval) {
          this.isRemovingAssignee.set(false);
        }
        // Reset assignee assignment loading state on error
        if (isAssigneeAssignment) {
          this.isAssigningUsers.set(false);
        }
      },
    });
  }

  // Delete approval method
  deleteApproval(approvalID: string) {
    this.isLoadingApprovals.set(true);

    const params = {
      approvalID: approvalID,
    };

    this.approvalService.deleteApproval(params).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message || 'Approval deleted successfully');
        // Close the delete confirmation popup
        this.showDeleteApprovalConfirmPopup.set(false);
        this.approvalToDelete.set(null);
        this.closeApprovalsModal();
        // Clear the single approval data since it's been deleted
        this.singleApprovalData.set(null);
        this.selectedApproval.set(null);
        this.approvalExists.set(false);
        // Immediately update itemDetails to clear approval-related fields for instant UI update
        const currentItemDetails = this.itemDetails();
        if (currentItemDetails) {
          this.itemDetails.set({
            ...currentItemDetails,
            approvalProcess: null,
            approvalName: null
          });
        }
        // Clear cache before refreshing to ensure fresh data
        const id = this.itemId();
        if (id) {
          if (this.service().clearSingleTaskCache) {
            this.service().clearSingleTaskCache(id);
          }
          if (this.service().clearSingleIssueCache) {
            this.service().clearSingleIssueCache(id);
          }
          if (this.service().clearSingleProjectCache) {
            this.service().clearSingleProjectCache(id);
          }
          if (this.service().clearSingleRevisionCache) {
            this.service().clearSingleRevisionCache(id);
          }
          if (this.service().clearSingleRequestCache) {
            this.service().clearSingleRequestCache(id);
          }
        }
        // Refresh the item details to reflect the change
        this.getItemDetails(this.itemId());
        this.getApprovalTracking(this.itemId());
        this.isLoadingApprovals.set(false);
        // Trigger refresh for approvals component
        this.approvalRefreshService.triggerRefresh();
      },
      error: (err: any) => {
        this.toastr.error(err?.message || 'Error deleting approval');
        // Close the delete confirmation popup on error too
        this.showDeleteApprovalConfirmPopup.set(false);
        this.approvalToDelete.set(null);
        this.isLoadingApprovals.set(false);
      },
    });
  }

  onStatusChange = (newStatus: string) => {
    // Prevent status change for parent tasks with children
   
      // Revert to original status
      const currentStatus = this.itemDetails()?.taskStatusData?._id ||
        this.itemDetails()?.issueStatusData?._id ||
        this.itemDetails()?.projectStatusData?._id ||
        this.itemDetails()?.revisionStatusData?._id ||
        this.itemDetails()?.requestStatusData?._id;
      this.selectedStatus.set(currentStatus);
     
  
    this.selectedStatus.set(newStatus);
    this.updateStatusListApproval();
    if (this.itemId()) {
      this.isUpdatingStatus.set(true);
      const params = {
        [this.getItemIdField()]: this.itemId(),
        [this.getStatusField()]: newStatus,
      };
      this.updateItem(params);
    }
  };

  onPlannedHoursChange = () => {
    if (!this.itemId()) {
      return;
    }
    const raw = this.submittedValue();
    const parsed = parseFloat(String(raw ?? '').replace(',', '.'));
    if (Number.isNaN(parsed)) {
      return;
    }
    const origRaw = this.originalPlannedHours();
    const origParsed = parseFloat(String(origRaw ?? '').replace(',', '.'));
    const unchanged =
      !Number.isNaN(origParsed) && parsed === origParsed
        ? true
        : String(raw ?? '').trim() === String(origRaw ?? '').trim();
    if (unchanged) {
      return;
    }
    this.isUpdatingPlannedHours.set(true);
    const params = {
      [this.getItemIdField()]: this.itemId(),
      plannedHours: parsed,
    };
    this.updateItem(params);
  };

  // Enhanced blur handler that's more intelligent about when to close
  onInputBlur = (event: FocusEvent) => {
    // Only close if we're not dragging and the focus is actually leaving the input area
    if (!this.isDragging()) {
      // Small delay to allow for potential focus changes within the same component
      setTimeout(() => {
        if (
          this.showInput() &&
          !this.inputBox?.nativeElement?.contains(document.activeElement)
        ) {
          this.submittedValue.set(this.inputValue() || this.submittedValue());
          this.showInput.set(false);
          this.onPlannedHoursChange();
        }
      }, 100);
    }
  };

  private getProjectedAiHoursInputValue(input: HTMLInputElement, char: string): string {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    return input.value.slice(0, start) + char + input.value.slice(end);
  }

  private formatAiHoursInputInProgress(raw: string): string {
    if (!raw) {
      return raw;
    }

    const v = raw.replace(/[^\d.]/g, '');
    const dotIndex = v.indexOf('.');
    if (dotIndex === -1) {
      return v;
    }

    const endsWithDot = v.endsWith('.');
    const intPart = dotIndex === 0 ? '' : v.slice(0, dotIndex).replace(/\./g, '');
    const fracPart = v.slice(dotIndex + 1).replace(/\./g, '').slice(0, 2);

    if (dotIndex === 0) {
      if (endsWithDot && fracPart.length === 0) {
        return '.';
      }
      return fracPart.length > 0 ? `.${fracPart}` : '.';
    }

    if (endsWithDot) {
      return `${intPart}.`;
    }

    return fracPart.length > 0 ? `${intPart}.${fracPart}` : intPart;
  }

  private wouldExceedAiHoursMax(value: string): boolean {
    const v = value.trim();
    if (v === '' || v === '.') {
      return false;
    }

    if (v.endsWith('.')) {
      const intPart = v.slice(0, -1);
      if (intPart && /^\d+$/.test(intPart)) {
        return parseFloat(intPart) > this.AI_HOURS_MAX;
      }
      return false;
    }

    if (/^\.\d{0,2}$/.test(v)) {
      return v === '.' ? false : parseFloat(`0${v}`) > this.AI_HOURS_MAX;
    }

    const normalized = v.startsWith('.') ? `0${v}` : v;
    const numericValue = parseFloat(normalized);
    return !Number.isNaN(numericValue) && numericValue > this.AI_HOURS_MAX;
  }

  private isValidAiHoursInput(value: string): boolean {
    const v = value.trim();
    if (v === '') {
      return true;
    }

    if (/^\d+$/.test(v)) {
      return !this.wouldExceedAiHoursMax(v);
    }

    if (/^\d+\.$/.test(v)) {
      return !this.wouldExceedAiHoursMax(v.slice(0, -1));
    }

    if (/^\.\d{0,2}$/.test(v)) {
      if (v === '.') {
        return true;
      }
      return parseFloat(`0${v}`) <= this.AI_HOURS_MAX;
    }

    if (!/^(\d{1,3})?(\.\d{1,2})?$/.test(v)) {
      return false;
    }

    return !this.wouldExceedAiHoursMax(v);
  }

  private normalizeAiHoursInputChange(raw: string, previousValue: string): string {
    const formatted = this.formatAiHoursInputInProgress(raw);
    if (!this.isValidAiHoursInput(formatted)) {
      return previousValue;
    }
    return formatted;
  }

  private applyAiHoursPaste(event: ClipboardEvent, setValue: (value: string) => void): void {
    event.preventDefault();
    const paste = (event.clipboardData || (window as any).clipboardData)?.getData('text')?.trim();
    const target = event.target as HTMLInputElement;
    if (!target || !paste) {
      return;
    }

    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? target.value.length;
    const projected = this.formatAiHoursInputInProgress(
      target.value.slice(0, start) + paste + target.value.slice(end)
    );

    if (!this.isValidAiHoursInput(projected)) {
      return;
    }

    setValue(projected);
  }

  onAiHoursInputChange(value: string): void {
    this.aiHoursInputValue.set(this.normalizeAiHoursInputChange(value, this.aiHoursInputValue()));
  }

  onAiHoursLogEntryInputChange(value: string): void {
    this.editingAiHoursLogEntryValue.set(
      this.normalizeAiHoursInputChange(value, this.editingAiHoursLogEntryValue())
    );
  }

  // AI hours formatting helper
  formatAiHoursValue(value: any): string {
    if (value === undefined || value === null || value === '') {
      return '0';
    }
    
    const numericValue = parseFloat(String(value));
    if (isNaN(numericValue)) {
      return '0';
    }
    
    // Round to 2 decimal places
    const roundedValue = Math.round(numericValue * 100) / 100;
    
    // Format to 2 decimal places
    const formattedValue = roundedValue.toFixed(2);
    
    // Remove trailing zeros after decimal and unnecessary decimal point
    return formattedValue.replace(/\.?0+$/, '');
  }

  // AI hours inline edit helpers
  isAiHoursEmptyOrZero(): boolean {
    const v = this.aiHoursValue();
    return v === '' || v === '0';
  }

  startAiHoursEdit = (prefillValue?: string) => {
    if (!this.canModifyAiHours()) {
      return;
    }
    const currentValue = this.aiHoursValue();
    this.originalAiHours.set(currentValue);
    
    // Default is new entry; prefill is used when editing from history slider.
    this.aiHoursInputValue.set(prefillValue ?? '');
    
    // Track the current value to display on the left side
    this.mostRecentEditedValue.set(currentValue);
    
    this.aiHoursEditing.set(true);
    requestAnimationFrame(() => {
      this.aiHoursInputRef?.nativeElement?.focus();
    });
  };

  private static readonly AI_HOURS_LOG_BODY_CLASS = 'ai-hours-log-slider-open';

  openAiHoursLogPanel(event?: MouseEvent): void {
    event?.stopPropagation();
    this.showAiHoursLogPanel.set(true);
    document.body.classList.add(StatusSidebar.AI_HOURS_LOG_BODY_CLASS);
    if (this.aiHoursLog().length === 0 && !this.isLoadingAiHoursLog()) {
      this.loadAiHoursLog();
    }
  }

  closeAiHoursLogPanel(): void {
    this.showAiHoursLogPanel.set(false);
    document.body.classList.remove(StatusSidebar.AI_HOURS_LOG_BODY_CLASS);
    this.cancelAiHoursLogEntryEdit();
  }

  editAiHoursLogEntry(trackId: string): void {
    const target = this.aiHoursDisplayRows().find((row) => row.trackId === trackId);
    if (!target) return;
    this.editingAiHoursLogEntryId.set(trackId);
    this.editingAiHoursLogEntryValue.set(this.formatAiHoursValue(target.hourSaved));
  }

  submitAiHoursLogEntryEdit(trackId: string): void {
    if (this.editingAiHoursLogEntryId() !== trackId) return;
    const val = this.editingAiHoursLogEntryValue().trim();
    const finalValue = val === '' ? '0' : val;
    const formatRegex = /^(\d{1,3})?(\.\d{1,2})?$/;
    if (!formatRegex.test(finalValue) || finalValue === '.') {
      this.toastr.error('Please enter a valid value (up to 3 digits and 2 decimals).');
      return;
    }
    const numeric = parseFloat(finalValue);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      this.toastr.error('AI hour value should be greater than 0.');
      return;
    }
    if (numeric > this.AI_HOURS_MAX) {
      this.toastr.error(`AI hour value cannot exceed ${this.AI_HOURS_MAX}.`);
      return;
    }
    const target = this.aiHoursDisplayRows().find((row) => row.trackId === trackId);
    if (!target) return;
    const targetId = target?.log?._id ?? target?.log?.id ?? null;
    const targetTime = target.createdAt?.getTime() ?? 0;
    const targetUser = this.getUserName(target.log);
    let updated = false;
    this.aiHoursLog.update((list) =>
      list.map((log: any) => {
        if (updated) return log;
        const logId = log?._id ?? log?.id ?? null;
        const logTime = this.getAiHoursLogTimestampValue(log);
        const sameById = targetId && logId && String(logId) === String(targetId);
        const sameByFallback =
          !targetId &&
          logTime === targetTime &&
          this.getUserName(log) === targetUser;
        if (sameById || sameByFallback) {
          updated = true;
          return {
            ...log,
            hourSaved: this.formatAiHoursValue(numeric),
          };
        }
        return log;
      })
    );
    if (updated) {
      this.toastr.success('AI hour entry updated.');
      this.applyAiHoursDisplayFromLog();
    }
    this.cancelAiHoursLogEntryEdit();
  }

  cancelAiHoursLogEntryEdit(): void {
    this.editingAiHoursLogEntryId.set(null);
    this.editingAiHoursLogEntryValue.set('');
  }

  deleteAiHoursLogEntry(trackId: string, event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.canModifyAiHours() || this.deletingAiHoursLogEntryId() !== null) {
      return;
    }

    this.cancelPendingAiHoursBlurSave();

    const target = this.aiHoursDisplayRows().find((row) => row.trackId === trackId);
    if (!target) return;

    const entryId = target.log?._id ?? target.log?.id ?? null;
    if (!entryId) {
      this.toastr.error('Unable to remove this entry.');
      return;
    }

    if (this.editingAiHoursLogEntryId() === trackId) {
      this.cancelAiHoursLogEntryEdit();
    }

    this.deletingAiHoursLogEntryId.set(trackId);

    this.apiService.removeAiHour(String(entryId)).subscribe({
      next: (res: any) => {
        this.removeAiHoursLogEntryFromList(trackId);
        const remainingSum = this.sumAiHoursLogRowHours();
        if (remainingSum === 0) {
          this.aiHoursValue.set('0');
          this.originalAiHours.set('0');
        } else {
          this.applyAiHoursDisplayFromLog();
        }

        this.clearItemDetailsCache();
        this.deletingAiHoursLogEntryId.set(null);
        this.refreshAiHoursProjectCount();
        this.toastr.success(res?.message || 'AI hour entry removed.');
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message || 'Failed to remove AI hour entry.');
        this.deletingAiHoursLogEntryId.set(null);
      },
    });
  }

  isAiHoursLogEntryDeleting(trackId: string): boolean {
    return this.deletingAiHoursLogEntryId() === trackId;
  }

  private sumAiHoursLogRowHours(): number {
    return Math.round(
      this.aiHoursLogTableRows().reduce((total, row) => total + row.hourSaved, 0) * 100
    ) / 100;
  }

  /** Keep sidebar AI Hours readout in sync with log table totals (when log has rows). */
  private applyAiHoursDisplayFromLog(): void {
    if (this.aiHoursLogTableRows().length === 0) {
      return;
    }
    const formatted = this.formatAiHoursValue(this.sumAiHoursLogRowHours());
    this.aiHoursValue.set(formatted);
    this.originalAiHours.set(formatted);
  }

  private clearItemDetailsCache(): void {
    const service = this.service();
    const id = this.itemId();
    if (!id) return;
    if (service.clearSingleTaskCache) {
      service.clearSingleTaskCache(id);
    }
    if (service.clearSingleIssueCache) {
      service.clearSingleIssueCache(id);
    }
    if (service.clearSingleProjectCache) {
      service.clearSingleProjectCache(id);
    }
    if (service.clearSingleRevisionCache) {
      service.clearSingleRevisionCache(id);
    }
    if (service.clearSingleRequestCache) {
      service.clearSingleRequestCache(id);
    }
  }

  private refreshAiHoursProjectCount(): void {
    const currentItemId = this.itemId();
    const objCode = this.config()?.type?.toUpperCase();
    if (currentItemId && objCode) {
      this.submenuCountService.refreshProjectHours(currentItemId, objCode);
    }
  }

  private removeAiHoursLogEntryFromList(trackId: string): boolean {
    if (this.editingAiHoursLogEntryId() === trackId) {
      this.cancelAiHoursLogEntryEdit();
    }

    const targetRow = this.buildAiHoursTableRows(this.aiHoursLog()).find(
      (row) => row.trackId === trackId
    );
    if (!targetRow) return false;

    const targetLog = targetRow.log;
    const targetId = targetLog?._id ?? targetLog?.id ?? null;
    const targetTime = this.getAiHoursLogTimestampValue(targetLog);
    let removed = false;

    this.aiHoursLog.update((list) =>
      list.filter((log: any) => {
        if (removed) return true;
        const isSameRef = log === targetLog;
        const logId = log?._id ?? log?.id ?? null;
        const logTime = this.getAiHoursLogTimestampValue(log);
        const sameById = targetId && logId && String(logId) === String(targetId);
        const sameByTime =
          !targetId && logTime === targetTime && log === targetLog;
        if (isSameRef || sameById || sameByTime) {
          removed = true;
          return false;
        }
        return true;
      })
    );

    return removed;
  }

  private cancelPendingAiHoursBlurSave(): void {
    if (this.aiHoursBlurSaveTimeout) {
      clearTimeout(this.aiHoursBlurSaveTimeout);
      this.aiHoursBlurSaveTimeout = null;
    }
    if (this.aiHoursEditing()) {
      this.aiHoursEditing.set(false);
      this.aiHoursFocused.set(false);
    }
  }

  saveAiHours = () => {
    if (this.deletingAiHoursLogEntryId() !== null) {
      return;
    }
    if (this.aiHoursBlurSaveTimeout) {
      clearTimeout(this.aiHoursBlurSaveTimeout);
      this.aiHoursBlurSaveTimeout = null;
    }
    const val = this.aiHoursInputValue().trim();
    const finalValue = val === '' ? '0' : val;
    
    // Validate format: max 3 digits before decimal, max 2 digits after decimal
    // Allow values starting with decimal point like .5, .05, .25
    const formatRegex = /^(\d{1,3})?(\.\d{1,2})?$/;
    if (!formatRegex.test(finalValue) || finalValue === '.') {
      // If invalid format, reset to original value
      this.aiHoursValue.set(this.originalAiHours());
      this.aiHoursEditing.set(false);
      this.aiHoursFocused.set(false);
      this.mostRecentEditedValue.set(null);
      return;
    }
    
    // Prevent saving if value is effectively 0 (including .0000, 0.00, etc.)
    const numericValue = parseFloat(finalValue);
    if (numericValue === 0 || isNaN(numericValue)) {
      this.aiHoursValue.set(this.originalAiHours());
      this.aiHoursEditing.set(false);
      this.aiHoursFocused.set(false);
      this.mostRecentEditedValue.set(null);
      return;
    }

    if (numericValue > this.AI_HOURS_MAX) {
      this.toastr.error(`AI hour value cannot exceed ${this.AI_HOURS_MAX}.`);
      this.aiHoursValue.set(this.originalAiHours());
      this.aiHoursEditing.set(false);
      this.aiHoursFocused.set(false);
      this.mostRecentEditedValue.set(null);
      return;
    }
    
    // Check if value actually changed to prevent unnecessary API calls
    // if (finalValue === this.originalAiHours()) {
    //   // No change, just close editing mode
    //   this.aiHoursValue.set(finalValue);
    //   this.aiHoursEditing.set(false);
    //   this.aiHoursFocused.set(false);
    //   // Clear the most recent edited value
    //   this.mostRecentEditedValue.set(null);
    //   return;
    // }
    
    this.aiHoursValue.set(finalValue);
    this.aiHoursEditing.set(false);
    this.aiHoursFocused.set(false);
    this.isUpdatingAiHours.set(true);
    
    // Get project ID and object code for the new API
    const projectId = this.itemDetails()?.projectID || this.itemDetails()?.projectId;
    const objCode = this.config()?.type?.toUpperCase();
    const objId = this.itemId();
    
    // Validate required parameters before making API call
    if (!objId) {
      this.aiHoursValue.set(this.originalAiHours());
      return;
    }
    
    if (!projectId) {
      this.isUpdatingAiHours.set(false);
      this.aiHoursValue.set(this.originalAiHours());
      return;
    }
    
    if (!objCode) {
      this.isUpdatingAiHours.set(false);
      this.aiHoursValue.set(this.originalAiHours());
      return;
    }
    
    // Persist AI hours via dedicated AI hour endpoint
    this.apiService.enterAiHour(objId, projectId, objCode, finalValue).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message || 'AI hours updated successfully');
        // Clear the cache for this item so that getItemDetails fetches fresh data
        const service = this.service();
        if (service.clearSingleTaskCache) {
          service.clearSingleTaskCache(this.itemId());
        }
        if (service.clearSingleIssueCache) {
          service.clearSingleIssueCache(this.itemId());
        }
        if (service.clearSingleProjectCache) {
          service.clearSingleProjectCache(this.itemId());
        }
        if (service.clearSingleRevisionCache) {
          service.clearSingleRevisionCache(this.itemId());
        }
        if (service.clearSingleRequestCache) {
          service.clearSingleRequestCache(this.itemId());
        }
        this.getItemDetails(this.itemId());
        this.isUpdatingAiHours.set(false);
        
        // Clear AI hours log cache to refresh on next hover
        this.aiHoursLog.set([]);
        const currentItemId = this.itemId();
        if (currentItemId) {
          this.submenuCountService.refreshProjectHours(currentItemId, objCode);
        }
        // Clear the most recent edited value after save
        this.mostRecentEditedValue.set(null);
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message || 'Failed to update AI hours');
        this.isUpdatingAiHours.set(false);
        // Revert to original value on error
        this.aiHoursValue.set(this.originalAiHours());
      }
    });
  };

  cancelAiHoursEdit = () => {
    if (this.aiHoursBlurSaveTimeout) {
      clearTimeout(this.aiHoursBlurSaveTimeout);
      this.aiHoursBlurSaveTimeout = null;
    }
    this.aiHoursInputValue.set(this.originalAiHours());
    this.aiHoursValue.set(this.originalAiHours());
    this.aiHoursEditing.set(false);
    this.aiHoursFocused.set(false);
    // Clear the most recent edited value
    this.mostRecentEditedValue.set(null);
  };

  onAiHoursKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelAiHoursEdit();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.saveAiHours();
      return;
    }
  };

  onAiHoursBlur = () => {
    this.aiHoursFocused.set(false);
    if (this.deletingAiHoursLogEntryId() !== null) {
      return;
    }
    if (this.aiHoursBlurSaveTimeout) clearTimeout(this.aiHoursBlurSaveTimeout);
    this.aiHoursBlurSaveTimeout = setTimeout(() => {
      this.aiHoursBlurSaveTimeout = null;
      if (this.deletingAiHoursLogEntryId() !== null) {
        return;
      }
      this.saveAiHours();
    }, 100);
  };

  private handleAiHoursInputKeypress(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const inputChar = event.key;
    if (inputChar.length !== 1) {
      return;
    }

    if (!/[\d.]/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    const currentValue = input.value || '';
    const projected = this.getProjectedAiHoursInputValue(input, inputChar);

    if (!this.isValidAiHoursInput(projected) || this.wouldExceedAiHoursMax(projected)) {
      event.preventDefault();
      return;
    }

    if (inputChar !== '.' && !currentValue.includes('.') && currentValue === '' && inputChar === '0') {
      event.preventDefault();
    }
  }

  onAiHoursKeypress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.saveAiHours();
      this.aiHoursEditing.set(false);
      return;
    }

    this.handleAiHoursInputKeypress(event);
  };

  onAiHoursLogEntryKeypress = (event: KeyboardEvent) => {
    this.handleAiHoursInputKeypress(event);
  };

  onAiHoursPaste = (event: ClipboardEvent) => {
    this.applyAiHoursPaste(event, (value) => this.aiHoursInputValue.set(value));
  };

  onAiHoursLogEntryPaste = (event: ClipboardEvent) => {
    this.applyAiHoursPaste(event, (value) => this.editingAiHoursLogEntryValue.set(value));
  };

  // AI hours log methods
  onAiHoursLogEnter = () => {
    if (this.aiHoursLogTooltipHideTimeout) {
      clearTimeout(this.aiHoursLogTooltipHideTimeout);
      this.aiHoursLogTooltipHideTimeout = null;
    }
    // Only load data if not already loaded and empty
    if (!this.showAiHoursLog() && this.aiHoursLog().length === 0) {
      this.loadAiHoursLog();
    }
    this.showAiHoursLog.set(true);
    this.aiHoursLogTooltipPositioned.set(false);
    this.aiHoursLogTooltipPositionRetryCount = 0;
    
    requestAnimationFrame(() => {
      this.positionAiHoursLogTooltip();
    });
  };

  onAiHoursLogLeave = (event?: MouseEvent) => {
    if (event && 'buttons' in event && (event as MouseEvent).buttons !== 0) {
      const onUp = () => this.scheduleAiHoursLogTooltipHide();
      document.addEventListener('pointerup', onUp, { once: true });
      return;
    }
    this.scheduleAiHoursLogTooltipHide();
  };

  private scheduleAiHoursLogTooltipHide(): void {
    if (this.aiHoursLogTooltipHideTimeout) {
      clearTimeout(this.aiHoursLogTooltipHideTimeout);
    }
    this.aiHoursLogTooltipHideTimeout = setTimeout(() => {
      this.aiHoursLogTooltipHideTimeout = null;
      this.showAiHoursLog.set(false);
    }, this.hintTooltipHideDelayMs);
  }

  positionAiHoursLogTooltip() {
    if (!this.aiHoursLogIconWrapperRef || !this.aiHoursLogTooltipRef) {
      if (this.aiHoursLogTooltipPositionRetryCount < this.aiHoursLogTooltipPositionMaxRetries) {
        this.aiHoursLogTooltipPositionRetryCount++;
        setTimeout(() => this.positionAiHoursLogTooltip(), 20);
      }
      return;
    }
    const iconRect = this.aiHoursLogIconWrapperRef.nativeElement.getBoundingClientRect();
    const tooltipEl = this.aiHoursLogTooltipRef.nativeElement as HTMLElement;
    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;
    const gap = 2;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = iconRect.bottom + gap;
    // For right sidebar, it's safer to right-align the tooltip to the icon to avoid cutoff by sidebar left edge
    let left = iconRect.right - tooltipWidth;

    // Horizontal adjustments - Ensure it doesn't go off-screen to the left
    if (left < 8) left = 8;
    // Ensure it doesn't go off-screen to the right
    if (left + tooltipWidth > viewportWidth - 8) {
      left = viewportWidth - tooltipWidth - 8;
    }

    // Vertical adjustments
    if (top + tooltipHeight > viewportHeight - 8) {
      top = iconRect.top - tooltipHeight - gap;
      if (top < 8) top = 8;
    }

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    this.aiHoursLogTooltipPositioned.set(true);
  }

  loadAiHoursLog = () => {
    const itemId = this.itemId();
    if (!itemId) return;

    this.isLoadingAiHoursLog.set(true);
    
    // Determine object code based on config type
    let objCode = 'TASK'; // default
    const configType = this.config().type;
    if (configType === 'issue') objCode = 'ISSUE';
    else if (configType === 'project') objCode = 'PROJECT';
    else if (configType === 'revision') objCode = 'REVISION';
    else if (configType === 'request') objCode = 'REQUEST';

    this.apiService.getAiHoursLog(itemId, objCode).subscribe({
      next: (res: any) => {
        this.isLoadingAiHoursLog.set(false);
        this.aiHoursLogHasLoaded.set(true);
        if (res?.success && res?.data != null) {
          this.aiHoursLog.set(this.normalizeAiHoursLogPayload(res.data));
        } else {
          this.aiHoursLog.set([]);
        }
        this.applyAiHoursDisplayFromLog();
      },
      error: (err: any) => {
        this.isLoadingAiHoursLog.set(false);
        this.aiHoursLogHasLoaded.set(true);
        console.error('Error loading AI hours log:', err);
        this.aiHoursLog.set([]);
        this.applyAiHoursDisplayFromLog();
      }
    });
  };

  // Load AI hours log when sidebar is loaded (if not new status)
  loadAiHoursLogOnLoad = (data?: any) => {
    // Check status from API response data first, then fallback to signal
    let statusValue = this.selectedStatusValue();
    
    // If signal is not set yet, try to get status from the API response data
    if (!statusValue && data) {
      statusValue = data.taskStatusData?.statusName ||
                   data.issueStatusData?.statusName ||
                   data.projectStatusData?.statusName ||
                   data.revisionStatusData?.statusName ||
                   data.requestStatusData?.statusName;
    }
    
    if (statusValue && statusValue.toLowerCase() !== 'new' && this.itemId()) {
      this.loadAiHoursLog();
    }
  };

  private normalizeAiHoursLogPayload(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === 'object') {
      if (Array.isArray(data.logs)) return data.logs;
      if (Array.isArray(data.records)) return data.records;
      if (Array.isArray(data.items)) return data.items;
      if (Array.isArray(data.history)) return data.history;
      if (Array.isArray(data.data)) return data.data;
    }
    return [];
  }

  private parseAiHoursNumeric(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const n = parseFloat(String(value).replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : null;
  }

  private readDirectHourSaved(log: any): number | null {
    if (!log || typeof log !== 'object') return null;
    const keys = [
      'hourSaved',
      'hoursSaved',
      'savedHours',
      'hoursDelta',
      'deltaHours',
      'hourDelta',
      'changeHours',
      'savedHour',
    ];
    for (const k of keys) {
      if (log[k] !== undefined && log[k] !== null && log[k] !== '') {
        const n = this.parseAiHoursNumeric(log[k]);
        if (n != null) return n;
      }
    }
    return null;
  }

  private getAiHoursLogTimestampValue(log: any): number {
    const raw =
      log?.createdAt ??
      log?.updatedAt ??
      log?.modifiedAt ??
      log?.addedOn ??
      null;
    if (!raw) return 0;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : 0;
  }

  private buildAiHoursTableRows(logs: any[]): {
    log: any;
    hourSaved: number;
    createdAt: Date | null;
    trackId: string;
  }[] {
    if (!Array.isArray(logs) || logs.length === 0) return [];
    const sorted = [...logs].sort(
      (a, b) =>
        this.getAiHoursLogTimestampValue(a) - this.getAiHoursLogTimestampValue(b)
    );
    let runningCumulative = 0;
    const out: {
      log: any;
      hourSaved: number;
      createdAt: Date | null;
      trackId: string;
    }[] = [];
    let idx = 0;
    for (const log of sorted) {
      const prev = this.parseAiHoursNumeric(
        log?.prevAiHours ?? log?.oldValue ?? log?.previousAiHours
      );
      const curr = this.parseAiHoursNumeric(
        log?.aiHours ?? log?.newValue ?? log?.currentAiHours
      );
      const direct = this.readDirectHourSaved(log);

      let hourSaved = 0;
      if (direct != null) {
        hourSaved = Math.max(0, direct);
      } else if (prev != null && curr != null) {
        hourSaved = Math.max(0, curr - prev);
      } else if (curr != null) {
        hourSaved = Math.max(0, curr - runningCumulative);
      }

      if (curr != null) {
        runningCumulative = curr;
      } else {
        runningCumulative += hourSaved;
      }

      const rawDate =
        log?.createdAt ??
        log?.updatedAt ??
        log?.modifiedAt ??
        log?.addedOn ??
        null;
      const createdAt = rawDate ? new Date(rawDate) : null;
      const trackId = `${String(log?._id ?? log?.id ?? idx)}-${this.getAiHoursLogTimestampValue(log)}`;
      idx++;
      out.push({
        log,
        hourSaved: Math.round(hourSaved * 100) / 100,
        createdAt,
        trackId,
      });
    }
    return out;
  }

  // Helper method to get user name from log entry
  getUserName = (log: any): string => {
    if (log?.createdBy?.name) {
      return log.createdBy.name;
    }
    if (typeof log?.changedBy === 'string' && log.changedBy.trim()) {
      return log.changedBy.trim();
    }
    if (typeof log?.userName === 'string' && log.userName.trim()) {
      return log.userName.trim();
    }
    if (typeof log?.createdByName === 'string' && log.createdByName.trim()) {
      return log.createdByName.trim();
    }
    // If the log entry has updatedUser object with name, use it
    if (log?.updatedUser?.name) {
      return log.updatedUser.name;
    }
    
    // Fallback to checking user ID in assigned users list
    if (log?.updatedUserID) {
      const assignedUser = this.assignedUsers().find(user => user._id === log.updatedUserID);
      if (assignedUser) {
        return assignedUser.name;
      }
      
      // Check if user exists in all assignable data
      const allUser = this.allAssignableData().find(user => user._id === log.updatedUserID);
      if (allUser) {
        return allUser.name;
      }
      
      // If not found in lists, return the user ID as fallback
      return log.updatedUserID;
    }
    
    return 'Unknown';
  };

  onApprovalChange = (approvalId: string) => {
    // Clear backup when switching to different approval
    this.originalApprovalData.set(null);
    this.selectedApproval.set(approvalId);
    this.applyAutoFilledApprovers(this.getSelectedApprovalDetails());
    // Create backup after a brief delay to ensure the approval is loaded
    setTimeout(() => {
      const currentApprovalData = this.getSelectedApprovalDetails();
      if (currentApprovalData) {
        this.originalApprovalData.set(JSON.parse(JSON.stringify(currentApprovalData)));
      }
    }, 100);
  };

  onApprovalStatusChange = (newStatus: string) => {
    this.selectedStatusApproval.set(newStatus);
  };

  goBackToApprovalSelection = () => {
    this.selectedApproval.set(null);
    this.singleApprovalData.set(null);
    this.showApprovalValidationErrors.set(false);
    // Clear original data when going back to selection
    this.originalApprovalData.set(null);
  };

  selectTab = (tab: 'manage_item' | 'time_summary') => {
    this.selectedTab.set(tab);
    
    if (tab === 'time_summary' && this.itemId() && this.config().showTimeSummary) {
      // Destroy existing charts first
      this.destroyCharts();
      // Load data and initialize charts
      this.loadTaskTimeSummary();
    }
  };

  ngAfterViewInit(): void {
    // Setup cross-tab sync listeners for request and revision activity updates
    this.setupCrossTabSync();

    // Add resize listener for charts
    fromEvent(window, 'resize').pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.selectedTab() === 'time_summary') {
        this.forceChartInitialization();
      }
    });

    // Watch for changes in cached hour data and refresh time summary if tab is active
    // Use interval to check for cache updates (polling approach to avoid infinite loops)
    // Check every 1 second to balance responsiveness with performance
    interval(1000).pipe(
      takeUntil(this.destroy$),
      filter(() => {
        // Only check when time summary tab is active
        const shouldCheck = this.selectedTab() === 'time_summary' && 
                           this.itemId() !== null && 
                           this.config().showTimeSummary &&
                           !this.isProcessingCacheUpdate;
        return shouldCheck === true;
      })
    ).subscribe(() => {
      this.checkAndUpdateFromCache();
    });
  }

  /**
   * Setup cross-tab synchronization for request and revision activity updates
   */
  private setupCrossTabSync(): void {
    this.crossTabSyncService.getSyncEvents()
      .pipe(
        takeUntil(this.destroy$),
        filter((event): event is any => !!event && !!event.type)
      )
      .subscribe((event) => {
        const currentItemId = this.itemId();
        const currentConfig = this.config();
        
        if (!currentItemId) {
          return;
        }

        // Approve/reject from approvals widget (or another tab) — refresh this item's approval UI
        if (event.type === 'approval_action_completed') {
          const eventObjId = event.data?.objID || event.data?.objId;
          const eventObjCode = (event.data?.objCode || '').toUpperCase();
          const currentObjCode = this.getItemIdFieldCode();
          if (
            eventObjId === currentItemId &&
            (!eventObjCode || eventObjCode === currentObjCode)
          ) {
            this.refreshApprovalUiAfterAction();
          }
          return;
        }

        // Handle request events
        if (currentConfig.type === 'request') {
          const eventRequestId = event.requestId || event.data?.requestId;
          
          // Only process events for the current request
          if (eventRequestId === currentItemId) {
            switch (event.type) {
              case 'request_activity_updated':
                // Refresh request details when updates are created/edited in other tabs
                if ('clearSingleRequestCache' in this.service() && typeof (this.service() as any).clearSingleRequestCache === 'function') {
                  (this.service() as any).clearSingleRequestCache(currentItemId);
                }
                this.getItemDetails(currentItemId);
                this.getApprovalTracking(currentItemId);
                break;
             case 'request_updated':
                // Handle general request updates (name, description, etc.)
                if ('clearSingleRequestCache' in this.service() && typeof (this.service() as any).clearSingleRequestCache === 'function') {
                  (this.service() as any).clearSingleRequestCache(currentItemId);
                }
                this.getItemDetails(currentItemId);
                this.getApprovalTracking(currentItemId);
                break;
              case 'request_status_updated':
                // Handle request status changes
                if ('clearSingleRequestCache' in this.service() && typeof (this.service() as any).clearSingleRequestCache === 'function') {
                  (this.service() as any).clearSingleRequestCache(currentItemId);
                }
                this.getItemDetails(currentItemId);
                this.getApprovalTracking(currentItemId);
                break;
              case 'request_field_updated':
                // Handle request field updates (assignees, planned hours, AI hours, due date)
                if ('clearSingleRequestCache' in this.service() && typeof (this.service() as any).clearSingleRequestCache === 'function') {
                  (this.service() as any).clearSingleRequestCache(currentItemId);
                }
                this.getItemDetails(currentItemId);
                this.getApprovalTracking(currentItemId);
                break;
              case 'request_moved':
                // Handle request moved to another project
                if ('clearSingleRequestCache' in this.service() && typeof (this.service() as any).clearSingleRequestCache === 'function') {
                  (this.service() as any).clearSingleRequestCache(currentItemId);
                }
                this.getItemDetails(currentItemId);
                this.getApprovalTracking(currentItemId);
                break;
            }
          }
        }
        
        // Handle revision events
        if (currentConfig.type === 'revision') {
          const eventRevisionId = event.revisionId || event.data?.revisionId;
          
          // Only process events for the current revision
          if (eventRevisionId === currentItemId) {
            switch (event.type) {
              case 'revision_activity_updated':
              case 'revision_field_updated':
              case 'revision_status_updated':
                // Refresh revision details when updates/fields are changed in other tabs
                if ('clearSingleRevisionCache' in this.service() && typeof (this.service() as any).clearSingleRevisionCache === 'function') {
                  (this.service() as any).clearSingleRevisionCache(currentItemId);
                }
                this.getItemDetails(currentItemId);
                this.getApprovalTracking(currentItemId);
                break;
            }
          }
        }


        // Handle issue events
        if (currentConfig.type === 'issue') {
          const eventIssueId = event.issueId || event.data?.issueId;
          
          // Only process events for the current issue
          if (eventIssueId === currentItemId) {
            switch (event.type) {
              case 'issue_activity_updated':
              case 'issue_field_updated':
              case 'issue_status_updated':
                // Refresh issue details when updates/fields are changed in other tabs
                // Note: clearSingleIssueCache doesn't exist on IssueService, so we skip cache clearing
                this.getItemDetails(currentItemId);
                this.getApprovalTracking(currentItemId);
                break;
            }
          }
        }

      });
  }

  /**
   * Check for cached hour data updates and refresh if available
   */
  private checkAndUpdateFromCache(): void {
    if (this.isProcessingCacheUpdate) {
      return;
    }

    const itemId = this.itemId();
    const config = this.config();
    
    if (!itemId || !config.showTimeSummary) {
      return;
    }

    const cachedHours = this.projectHoursService.getCurrentProjectHours()();
    const objCode = config.type.toUpperCase();
    
    // Check if cached data exists and matches current item
    if (!cachedHours || !cachedHours.fullHourData) {
      return;
    }

    const isMatch = 
      (objCode === 'TASK' && cachedHours.taskID === itemId) ||
      (objCode === 'PROJECT' && cachedHours.projectID === itemId);
    
    if (!isMatch) {
      return;
    }

    // Create a hash of the data to check if it has changed
    const dataHash = this.createDataHash(cachedHours.fullHourData);
    
    // Only process if data has actually changed
    if (dataHash === this.lastProcessedDataHash) {
      return;
    }

    // Set flag to prevent concurrent processing
    this.isProcessingCacheUpdate = true;
    this.lastProcessedDataHash = dataHash;

    try {
      // Refresh the time summary data with cached data
      // Skip loading state since this is a reactive update from cache
      this.processHourSummaryData(cachedHours.fullHourData, true);
    } finally {
      // Reset flag after a short delay to allow processing to complete
      setTimeout(() => {
        this.isProcessingCacheUpdate = false;
      }, 100);
    }
  }

  /**
   * Create a hash of the hour data to detect changes
   */
  private createDataHash(data: any): string {
    if (!data) return '';
    return `${data.plannedHour || 0}_${data.onsetHours || 0}_${data.offsetHours || 0}`;
  }

  ngOnDestroy(): void {
    document.body.classList.remove(StatusSidebar.AI_HOURS_LOG_BODY_CLASS);
    this.destroyCharts();
    this.clearTimeouts();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Calculate task percentage for time summary
  private calculateTaskPercentage(taskData: any): void {
    if (!taskData) {
      this.taskPercentage.set(0);
      return;
    }

    this.taskPercentage.set(
      resolvePercentageComplete(
        taskData.percentageComplete,
        taskData.plannedHours ?? taskData.plannedHour,
        taskData.hourFilledData ?? taskData.filledHours ?? taskData.onsetHours
      )
    );
  }

  // Calculate task percentage from hour summary API response
  private calculateTaskPercentageFromApi(apiData: any): void {
    if (!apiData) {
      this.taskPercentage.set(0);
      return;
    }

    this.taskPercentage.set(
      resolvePercentageComplete(undefined, apiData.plannedHour, apiData.onsetHours)
    );
  }

  // Load task time summary data
  private loadTaskTimeSummary(): void {
    if (!this.itemId()) return;

    this.isTimeSummaryLoading.set(true);
    this.isChartsLoading.set(true);

    const config = this.config();
    const objCode = config.type.toUpperCase();
    const objID = this.itemId()!;
    
    // First, check if we have cached data from projectHoursService
    const cachedData = this.projectHoursService.getFullHourData(objID, objCode);
    
    if (cachedData) {
      // Use cached data instead of making API call
      const dataHash = this.createDataHash(cachedData);
      this.lastProcessedDataHash = dataHash;
      this.processHourSummaryData(cachedData);
      return;
    }
    
    // If no cached data, make API call
    const hourDataObservable = this.logtimeService.getHourData(objID, objCode);

    hourDataObservable.pipe(
      catchError(error => {
        console.error(`Error loading ${config.type} time summary:`, error);
        this.isTimeSummaryLoading.set(false);
        this.isChartsLoading.set(false);
        return [];
      })
    ).subscribe((response) => {
      if (response && response.data) {
        // Process the response data
        const dataHash = this.createDataHash(response.data);
        this.lastProcessedDataHash = dataHash;
        this.processHourSummaryData(response.data);
      } else {
        this.isTimeSummaryLoading.set(false);
        this.isChartsLoading.set(false);
      }
    });
  }

  /**
   * Process hour summary data and update component state
   * @param data - The hour summary data from API or cache
   * @param skipLoadingState - If true, don't update loading state (used for cache updates)
   */
  private processHourSummaryData(data: any, skipLoadingState: boolean = false): void {
    // Transform the API response to match our expected format
    const transformedData = {
      totalPlannedHours: data.plannedHour || 0,
      hourFilledData: data.onsetHours || 0,
      unfilledHours: (data.plannedHour || 0) - (data.onsetHours || 0),
      offsetHours: data.offsetHours || 0,
      // Additional fields for user input hours
      totalBillableHours: data.onsetHours || 0,
      totalTaskHours: data.onsetHours || 0,
      totalProjectHours: 0,
      totalRequestHours: 0,
      totalRevisionHours: 0,
      totalNonBillableHours: 0,
      totalIssueHours: 0,
      totalOffsetHours: data.offsetHours || 0
    };
    
    // Check if data has actually changed to avoid unnecessary chart re-initialization
    const currentData = this.taskHourData();
    const hasChanged = !currentData || 
      currentData.hourFilledData !== transformedData.hourFilledData ||
      currentData.totalPlannedHours !== transformedData.totalPlannedHours ||
      currentData.offsetHours !== transformedData.offsetHours;
    
    this.taskHourData.set(transformedData);
    this.taskHourStatus.set(transformedData);
    
    // Try to get percentageComplete from ProjectHoursService first (set by getItemDetails)
    const itemId = this.itemId();
    const config = this.config();
    if (itemId) {
      const objCode = config.type.toUpperCase();
      const storedPercentage = this.projectHoursService.getPercentageComplete(itemId, objCode);
      const apiValue =
        storedPercentage !== null && storedPercentage !== undefined
          ? storedPercentage
          : undefined;
      this.taskPercentage.set(
        resolvePercentageComplete(apiValue, data.plannedHour, data.onsetHours)
      );
    } else {
      this.calculateTaskPercentageFromApi(data);
    }
    
    if (!skipLoadingState) {
      this.isTimeSummaryLoading.set(false);
    }
    
    // Only re-initialize charts if data has changed or charts don't exist
    if (hasChanged || this.chartInstances.length === 0) {
      // Initialize charts after data is loaded with proper delay
      setTimeout(() => {
        this.initializeCharts();
      }, 200);
    }
  }

  // Initialize charts
  private async initializeCharts(): Promise<void> {
    if (!this.taskHourStatus() || !this.taskHourData()) {
      this.isChartsLoading.set(false);
      return;
    }

    try {
      this.destroyCharts();
      this.isChartsLoading.set(true);

      // Add timeout to hide spinner if charts take too long
      this.loadingTimeout = setTimeout(() => {
        if (this.isChartsLoading()) {
          this.isChartsLoading.set(false);
        }
      }, 5000);

      // Wait for DOM to be ready
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 100));

      // Ensure containers are available
      if (!this.taskStatusChartContainer?.nativeElement || !this.balanceHoursChartContainer?.nativeElement) {
        console.warn('Chart containers not available');
        this.isChartsLoading.set(false);
        return;
      }

      // Initialize task status chart
      this.initTaskStatusChart();
      // Initialize balance hours chart
      this.initBalanceHoursChart();

      this.isChartsLoading.set(false);
      this.clearTimeouts();
    } catch (error) {
      console.error('Error initializing charts:', error);
      this.isChartsLoading.set(false);
      this.hasChartError.set(true);
    }
  }

  // Initialize task status chart
  private initTaskStatusChart(): void {
    if (!this.taskStatusChartContainer?.nativeElement) return;

    const percentage = this.taskPercentage();
    if (percentage === undefined || percentage === null) return;

    try {
      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'solidgauge',
          backgroundColor: 'transparent',
          height: 100,
          width: 100,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { enabled: false },
        pane: {
          startAngle: -150,
          endAngle: 150,
          background: [{
            outerRadius: '100%',
            innerRadius: '88%',
            backgroundColor: '#A7BBCE',
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        series: [{
          type: 'solidgauge',
          name: 'Completion',
          data: [{
            y: percentage,
            color: '#F46D73',
            radius: '100%',
            innerRadius: '88%'
          }]
        }],
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: true,
              borderWidth: 0,
              useHTML: false,
              y: 0,
              verticalAlign: 'middle',
              align: 'center',
              formatter: function (this: any) {
                return `${this.y}%`;
              },
              style: {
                fontSize: '20px',
                fontWeight: '600',
                color: 'var(--color-text)',
                fontFamily: 'Outfit, sans-serif',
              }
            }
          }
        }
      };

      const chart = Highcharts.chart(this.taskStatusChartContainer.nativeElement, chartOptions);
      this.chartInstances.push(chart);
    } catch (error) {
      console.error('Error creating task status chart:', error);
    }
  }

  // Initialize balance hours chart
  private initBalanceHoursChart(): void {
    if (!this.balanceHoursChartContainer?.nativeElement) return;

    const hourStatus = this.taskHourStatus();
    if (!hourStatus || hourStatus.unfilledHours === undefined) return;

    try {
      const percent = Math.abs(hourStatus.unfilledHours) / (hourStatus.totalPlannedHours || 1) * 100;
      const redArcLength = Math.min(percent, 100);
      

      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'solidgauge',
          backgroundColor: 'transparent',
          height: 100,
          width: 100,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { enabled: false },
        pane: {
          startAngle: 0,
          endAngle: 360,
          background: [{
            outerRadius: '100%',
            innerRadius: '75%',
            backgroundColor: '#7cc74d',
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: true,
              borderWidth: 0,
              useHTML: true,
              y: 0,
              verticalAlign: 'middle',
              align: 'center',
              formatter: function (this: any) {
                const unfilledHours = hourStatus.unfilledHours || 0;
                return `<div style="text-align: center; font-family: 'Outfit', sans-serif;">
                          <div style="font-size: 9px; font-weight: 500; line-height: 1.1; color: var(--color-accent-new);">Balance<br>Hours</div>
                          <div style="font-size: 10px; font-weight: 600; color: var(--color-accent-new);">${unfilledHours.toFixed(2)}</div>
                        </div>`;
              }
            },
            linecap: 'round',
            rounded: true,
            stickyTracking: false
          }
        },
        series: [{
          type: 'solidgauge',
          name: 'Balance',
          data: [{
            y: redArcLength,
            color: '#4C8BF5',
            radius: '85%',
            innerRadius: '68%'
          }]
        }]
      };

      const chart = Highcharts.chart(this.balanceHoursChartContainer.nativeElement, chartOptions);
      this.chartInstances.push(chart);
      
      // Add manual text overlay as fallback
      setTimeout(() => {
        this.addBalanceHoursTextOverlay(hourStatus.unfilledHours);
      }, 100);
    } catch (error) {
      console.error('Error creating balance hours chart:', error);
    }
  }

  // Force chart initialization
  private forceChartInitialization(): void {
    this.destroyCharts();
    this.isChartsLoading.set(true);
    setTimeout(() => {
      if (this.selectedTab() === 'time_summary' && this.taskHourStatus()) {
        this.initializeCharts();
      }
    }, 200);
  }

  // Destroy charts
  private destroyCharts(): void {
    this.clearTimeouts();

    this.chartInstances.forEach(chart => {
      try {
        if (chart && chart.destroy && typeof chart.destroy === 'function') {
          chart.destroy();
        }
      } catch (error) {
        console.warn('Error destroying chart:', error);
      }
    });
    this.chartInstances = [];

    if (this.taskStatusChartContainer?.nativeElement) {
      this.taskStatusChartContainer.nativeElement.innerHTML = '';
    }
    if (this.balanceHoursChartContainer?.nativeElement) {
      this.balanceHoursChartContainer.nativeElement.innerHTML = '';
    }
    
    // Remove any text overlays
    const overlays = document.querySelectorAll('.balance-hours-overlay');
    overlays.forEach(overlay => overlay.remove());
  }

  // Clear timeouts
  private clearTimeouts(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    if (this.fallbackTimeout) {
      clearTimeout(this.fallbackTimeout);
      this.fallbackTimeout = null;
    }
    if (this.plannedHoursTooltipHideTimeout) {
      clearTimeout(this.plannedHoursTooltipHideTimeout);
      this.plannedHoursTooltipHideTimeout = null;
    }
    if (this.aiHoursLogTooltipHideTimeout) {
      clearTimeout(this.aiHoursLogTooltipHideTimeout);
      this.aiHoursLogTooltipHideTimeout = null;
    }
    if (this.copyFeedbackTimeout) {
      clearTimeout(this.copyFeedbackTimeout);
      this.copyFeedbackTimeout = null;
    }
  }

  // Add manual text overlay for balance hours chart
  private addBalanceHoursTextOverlay(unfilledHours: number): void {
    if (!this.balanceHoursChartContainer?.nativeElement) return;

    // Remove any existing overlay
    const existingOverlay = this.balanceHoursChartContainer.nativeElement.querySelector('.balance-hours-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }


    
  }

  getStatusCardTitle(): string {
    const config = this.config();
    const objectType = config.type.charAt(0).toUpperCase() + config.type.slice(1);
    return `${objectType} Hours And Status`;
  }

  getStatusChartCaption(): string {
    const config = this.config();
    const objectType = config.type.charAt(0).toUpperCase() + config.type.slice(1);
    return `${objectType} Status`;
  }

  /** Planned hours readout: number + unit, no awkward line breaks (see .ts_ph_value nowrap). */
  displayPlannedHours(): string {
    const v = this.submittedValue();
    if (v === null || v === undefined || v === '') {
      return '-';
    }
    const n =
      typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    if (Number.isNaN(n)) {
      return `${String(v)} Hrs`;
    }
    return `${this.formatHours(n)} Hrs`;
  }

  // Format hours for display
  formatHours(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }

    const absValue = Math.abs(value);
    const integerPart = Math.floor(absValue).toString();

    if (integerPart.length >= 4) {
      const truncatedInteger = integerPart.substring(0, 4);
      return (value < 0 ? '-' : '') + truncatedInteger;
    }

    const remainingDigits = Math.min(4 - integerPart.length, 2);

    if (remainingDigits <= 0) {
      return (value < 0 ? '-' : '') + integerPart;
    }

    const multiplier = Math.pow(10, remainingDigits);
    const roundedValue = Math.round(absValue * multiplier) / multiplier;

    let result = roundedValue.toFixed(remainingDigits);

    if (result.includes('.')) {
      result = result.replace(/\.?0+$/, '');
    }

    return (value < 0 ? '-' : '') + result;
  }
}
